import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from 'react';
import { receiptScannerService } from '../../services/receiptScannerService';
import { createReceipt, updateReceipt } from '../../services/receiptManagementService';
import { useReceiptScanner } from '../../Contexts/ReceiptScannerContext';
import VatContext from '../../Contexts/VatContext';
import { CATEGORY_OPTIONS as DEFAULT_CATEGORIES, getCategoryDisplayName, getCategoryIcon, normalizeCategoryId } from '../../lib/categories';
import { guardAutoDetectedReceiptExpenseCategory } from '../../lib/receiptAutoCategoryGuard';
import { parseLineItemPackDetails } from '../../lib/receiptLineItemUtils';
import { formatDateForInput } from '../../lib/dateUtils';
import {
  getCountryCurrencySymbol,
  getCountryVatRateProfile,
  getSelectedCountry,
  normalizeCurrencySymbol,
  normalizeVatDataForCountry,
  normalizeVatRateToCountry
} from '../../lib/receiptCountryUtils';
import { getCountryTerminology } from '../../lib/countrySettings';
import {
  getClientAllocationLabel,
  getTransactionClientAllocations,
  normalizeClientAllocations
} from '../../lib/clientUtils';
import { auth } from '../../firebase';
import {
  downloadDriveFileAsFile,
  openDriveReceiptPicker
} from '../../googleDriveService';

// Log when component is loaded
console.log('ReceiptScannerComponent loaded');

const VALID_RECEIPT_FILE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];

/**
 * Receipt Scanner Component
 * 
 * This component provides a UI for uploading and scanning receipts,
 * then converting them to transactions for the 70Lives Business Finance Tracker.
 * 
 * @param {Object} props
 * @param {Function} props.onReceiptProcessed - Callback when receipt is processed
 * @param {Function} props.onError - Callback for error handling
 * @param {string} props.userRole - User role for permission checks
 */
function ReceiptScannerComponent({
  onReceiptProcessed,
  onError = () => {},
  userRole = 'user',
  categories = DEFAULT_CATEGORIES,
  transactionType = 'expenses',
  clients = [],
  paymentMethods = [],
  rctSubcontractors = [],
  supportsRct = false,
  supportsProjects = false,
  onAddCategory = null,
  onAddClient = null,
  onAddSupplier = null,
  onReceiptAbandoned = null,
  onReceiptCommitted = null,
  activeWorkspace = null,
  initialDriveImportMode = null
}) {
  const normalizedTransactionType = transactionType === 'income' ? 'income' : 'expenses';
  const isIncomeUpload = normalizedTransactionType === 'income';
  const categoryOptions = Array.isArray(categories?.[normalizedTransactionType])
    ? categories[normalizedTransactionType]
    : [];
  const expenseCategoryOptions = Array.isArray(categories?.expenses) ? categories.expenses : [];
  const supplierOptions = (Array.isArray(rctSubcontractors) ? rctSubcontractors : [])
    .filter((supplier) => supportsRct || supplier.supplierType !== 'rct');
  const clientById = useMemo(
    () => new Map((Array.isArray(clients) ? clients : []).map((client) => [client.id, client])),
    [clients]
  );
  const getClientOptionLabel = (client = {}) => {
    const name = String(client.name || `${client.firstName || ''} ${client.lastName || ''}` || '').trim();
    return name || client.email || client.phone || 'Unnamed client';
  };
  const buildClientAllocationsFromIds = useCallback((clientIds = []) => (
    normalizeClientAllocations(
      (Array.isArray(clientIds) ? clientIds : [])
        .map((clientId) => ({ clientId }))
        .filter((entry) => entry.clientId),
      clientById
    )
  ), [clientById]);
  const getExpenseAllocationIds = useCallback((transaction = {}) => (
    getTransactionClientAllocations(transaction, clientById, 0)
      .map((allocation) => allocation.clientId)
      .filter(Boolean)
  ), [clientById]);
  const getExpenseAllocationLabel = useCallback((transaction = {}) => (
    getClientAllocationLabel(transaction, clientById)
  ), [clientById]);

  // Normalize OCR amounts to a clean numeric string
  const normalizeAmount = (value) => {
    if (value === null || value === undefined) return '';
    const cleaned = String(value).replace(/[^\d.,-]/g, '');
    const normalized = cleaned.includes(',') && cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
      ? cleaned.replace(/\./g, '').replace(/,/g, '.')
      : cleaned;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
  };

  const getTodayInputDate = () => new Date().toISOString().split('T')[0];

  const normalizeReceiptDateForInput = (value) => {
    const isoDate = formatDateForInput(value);
    if (!isoDate) return getTodayInputDate();

    const [year, month, day] = isoDate.split('-').map((part) => Number(part));
    const parsedDate = new Date(year, month - 1, day);
    const futureLimit = new Date();
    futureLimit.setDate(futureLimit.getDate() + 1);
    futureLimit.setHours(23, 59, 59, 999);

    if (Number.isNaN(parsedDate.getTime()) || parsedDate > futureLimit) {
      return getTodayInputDate();
    }

    return isoDate;
  };

  const RECEIPT_MONTHS = {
    jan: 1,
    january: 1,
    feb: 2,
    february: 2,
    mar: 3,
    march: 3,
    apr: 4,
    april: 4,
    may: 5,
    jun: 6,
    june: 6,
    jul: 7,
    july: 7,
    aug: 8,
    august: 8,
    sep: 9,
    sept: 9,
    september: 9,
    oct: 10,
    october: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12
  };

  const formatValidatedReceiptDateParts = (dayInput, monthInput, yearInput) => {
    const day = Number(dayInput);
    const month = Number(monthInput);
    const rawYear = Number(yearInput);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;
    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return '';
    if (year < 2000 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return '';

    const parsedDate = new Date(year, month - 1, day);
    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return '';
    }

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const extractReceiptDateForInputFromText = (text) => {
    if (!text) return '';

    const source = String(text);
    const lines = source
      .split(/\r?\n/)
      .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    const parseNamedDate = (line) => {
      const match = String(line || '').match(/\b(\d{1,2})\s+([A-Za-z]{3,9})[.,]?\s+(\d{2,4})\b/i);
      if (!match) return '';
      const month = RECEIPT_MONTHS[String(match[2] || '').toLowerCase()];
      return formatValidatedReceiptDateParts(match[1], month, match[3]);
    };

    const parseNumericDate = (line) => {
      const match = String(line || '').match(/\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/);
      return match ? formatValidatedReceiptDateParts(match[1], match[2], match[3]) : '';
    };

    for (let index = 0; index < lines.length; index += 1) {
      if (!/\bdate\s*of\s*issue\b/i.test(lines[index])) continue;

      const sameLineDate = parseNamedDate(lines[index]) || parseNumericDate(lines[index]);
      if (sameLineDate) return sameLineDate;

      for (let offset = 1; offset <= 4 && index + offset < lines.length; offset += 1) {
        const nearbyDate = parseNamedDate(lines[index + offset]) || parseNumericDate(lines[index + offset]);
        if (nearbyDate) return nearbyDate;
      }
    }

    const numericLabels = [
      /\border\s*date\b[^\n\r]{0,24}\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/i,
      /\b(?:date|invoice\s*date|transaction\s*date|date\s*of\s*issue)\b[^\n\r]{0,24}\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/i
    ];
    for (const pattern of numericLabels) {
      const match = source.match(pattern);
      if (!match) continue;
      const parsed = formatValidatedReceiptDateParts(match[1], match[2], match[3]);
      if (parsed) return parsed;
    }

    const namedLabels = [
      /\b(?:date|invoice\s*date|transaction\s*date|date\s*of\s*issue)\b[^\n\r]{0,24}\b(\d{1,2})\s+([A-Za-z]{3,9})[.,]?\s+(\d{2,4})\b/i
    ];
    for (const pattern of namedLabels) {
      const match = source.match(pattern);
      if (!match) continue;
      const month = RECEIPT_MONTHS[String(match[2] || '').toLowerCase()];
      const parsed = formatValidatedReceiptDateParts(match[1], month, match[3]);
      if (parsed) return parsed;
    }

    if (/\b(electric ireland|bill summary|billing period|total due|charges for this period)\b/i.test(source)) {
      const namedDateMatches = Array.from(source.matchAll(/\b(\d{1,2})\s+([A-Za-z]{3,9})[.,]?\s+(\d{2,4})\b/gi))
        .map((match) => {
          const month = RECEIPT_MONTHS[String(match[2] || '').toLowerCase()];
          const parsed = formatValidatedReceiptDateParts(match[1], month, match[3]);
          return parsed;
        })
        .filter(Boolean)
        .sort();
      if (namedDateMatches.length > 0) {
        return namedDateMatches[namedDateMatches.length - 1];
      }
    }

    const genericNumericMatch = source.match(/\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/);
    if (genericNumericMatch) {
      const parsed = formatValidatedReceiptDateParts(genericNumericMatch[1], genericNumericMatch[2], genericNumericMatch[3]);
      if (parsed) return parsed;
    }

    return '';
  };

  const parseAmountNumber = (value) => {
    const normalized = normalizeAmount(value);
    if (!normalized) return null;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parseVatRateNumber = (value) => {
    const parsed = parseAmountNumber(value);
    if (parsed === null || parsed < 0) return null;
    return parsed > 1 ? parsed / 100 : parsed;
  };

  const roundToTwoDecimals = (value) => parseFloat(value.toFixed(2));

  const extractInvoiceSummaryVatFromText = (rawText = '') => {
    const text = String(rawText || '').replace(/\r/g, '\n');
    if (!text) return null;

    const amountPattern = '([-+]?(?:[€£$]\\s*)?(?:\\d{1,3}(?:[.,]\\d{3})+|\\d+)(?:[.,]\\d{2})?)';
    const parseAmount = (value) => parseAmountNumber(value);
    const compactText = text
      .replace(/[ \t]+/g, ' ')
      .replace(/\n+/g, '\n');

    const parseMoneyValues = (value) => (String(value || '').match(/\d+[.,]\d{2}/g) || [])
      .map((token) => parseAmount(token))
      .filter((parsed) => Number.isFinite(parsed));

    const buildInvoiceSummary = ({ gross = null, net = null, vat = null, vatRate = null } = {}) => {
      const resolvedNet = Number.isFinite(net) ? net : null;
      const resolvedVat = Number.isFinite(vat) ? vat : null;
      let resolvedGross = Number.isFinite(gross) ? gross : null;

      if ((!Number.isFinite(resolvedGross) || resolvedGross <= 0) && Number.isFinite(resolvedNet) && Number.isFinite(resolvedVat)) {
        resolvedGross = roundToTwoDecimals(resolvedNet + resolvedVat);
      }

      if (!Number.isFinite(resolvedGross) || resolvedGross <= 0 || !Number.isFinite(resolvedVat) || resolvedVat < 0) {
        return null;
      }

      const rawRate = Number.isFinite(vatRate)
        ? vatRate
        : (Number.isFinite(resolvedNet) && resolvedNet > 0 && resolvedVat > 0 ? resolvedVat / resolvedNet : 0);
      const inferredRate = rawRate > 0
        ? (normalizeVatRateToCountry(rawRate, selectedCountry) ?? rawRate)
        : 0;

      return {
        gross: roundToTwoDecimals(resolvedGross),
        vatAmount: roundToTwoDecimals(resolvedVat),
        vatRate: inferredRate,
        vatComponents: resolvedVat > 0
          ? [{
              rate: inferredRate,
              amount: roundToTwoDecimals(resolvedVat),
              description: inferredRate > 0 ? `${(inferredRate * 100).toFixed(1)}% VAT` : 'VAT'
            }]
          : [],
        depositRefund: 0,
        forceReplaceItems: true
      };
    };

    const extractSequentialVatTableSummary = () => {
      const rateMatch = compactText.match(/\bvat\s*rate\b[\s\S]{0,40}?(\d{1,2}(?:[.,]\d{1,2})?)/i);
      const netMatch = compactText.match(/\bnet\s*goods\b(?!\s*total)[\s\S]{0,40}?(\d+[.,]\d{2})/i);
      if (!rateMatch || !netMatch) return null;

      const netSearchStart = (netMatch.index || 0) + netMatch[0].length;
      const vatAfterNetMatch = compactText
        .slice(netSearchStart)
        .match(/\bvat\b(?!\s*(?:code|rate|id|reg|registration|number|no\.?))[\s\S]{0,40}?(\d+[.,]\d{2})/i);
      if (!vatAfterNetMatch) return null;

      const ratePercent = parseFloat(String(rateMatch[1] || '').replace(',', '.'));
      const net = parseAmount(netMatch[1]);
      const vat = parseAmount(vatAfterNetMatch[1]);
      if (!Number.isFinite(ratePercent) || ratePercent < 0 || ratePercent > 100) return null;
      if (!Number.isFinite(net) || net <= 0 || !Number.isFinite(vat) || vat < 0) return null;

      const expectedVat = roundToTwoDecimals(net * (ratePercent / 100));
      if (Math.abs(expectedVat - vat) > Math.max(0.05, net * 0.005)) {
        return null;
      }

      const grossMatch = compactText.match(/\btotal\s*invoice\s*value\b[\s\S]{0,60}?(\d+[.,]\d{2})/i);
      const gross = grossMatch ? parseAmount(grossMatch[1]) : roundToTwoDecimals(net + vat);
      const snappedRate = normalizeVatRateToCountry(ratePercent / 100, selectedCountry);
      const inferredRate = snappedRate ?? ratePercent / 100;

      return buildInvoiceSummary({
        gross,
        net,
        vat,
        vatRate: inferredRate
      });
    };

    const sequentialVatTableSummary = extractSequentialVatTableSummary();
    if (sequentialVatTableSummary) return sequentialVatTableSummary;

    const extractExplicitVatTableSummary = () => {
      const hasVatTableSignal = /\b(vat|v\s*a\s*t|vai)\s*(code|rate)\b|\bIE0?1\b/i.test(compactText);
      if (!hasVatTableSignal) {
        return null;
      }

      const vatTableRowPattern = /\b[A-Z1I]{1,3}\d{0,3}\b\s+(\d{1,2}(?:[.,]\d+)?)\s+(\d+[.,]\d{2})\s+(\d+[.,]\d{2})/gi;
      let tableMatch;
      while ((tableMatch = vatTableRowPattern.exec(compactText)) !== null) {
        const ratePercent = parseFloat(String(tableMatch[1] || '').replace(',', '.'));
        const net = parseAmount(tableMatch[2]);
        const vat = parseAmount(tableMatch[3]);
        if (!Number.isFinite(ratePercent) || ratePercent < 0 || ratePercent > 100) continue;
        if (!Number.isFinite(net) || net <= 0 || !Number.isFinite(vat) || vat < 0) continue;

        const snappedRate = normalizeVatRateToCountry(ratePercent / 100, selectedCountry);
        const inferredRate = snappedRate ?? roundToTwoDecimals(ratePercent) / 100;
        return buildInvoiceSummary({
          gross: roundToTwoDecimals(net + vat),
          net,
          vat,
          vatRate: inferredRate
        });
      }

      const tableLines = compactText
        .split('\n')
        .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      const tableStart = tableLines.findIndex((line) =>
        /\b(vat|v\s*a\s*t|vai)\s*(code|rate)\b|\bIE0?1\b/i.test(line)
      );
      if (tableStart === -1) return null;

      const tableSection = [];
      for (let index = tableStart; index < tableLines.length && tableSection.length < 16; index += 1) {
        const line = tableLines[index];
        if (index > tableStart && /\b(your\s+vat\s+id|vat\s*reg|registered|company|address|page|iso\s*\d|thank\s+you)\b/i.test(line)) {
          break;
        }
        tableSection.push(line);
      }

      const sectionText = tableSection.join('\n');
      const moneyValues = parseMoneyValues(sectionText);
      const numericValues = (sectionText.match(/\d+(?:[.,]\d+)?/g) || [])
        .map((token) => parseFloat(String(token).replace(',', '.')))
        .filter((value) => Number.isFinite(value));
      const ratePercent = numericValues.find((value) =>
        [23, 13.5, 9, 0].some((allowedRate) => Math.abs(value - allowedRate) <= 0.02)
      );
      if (!Number.isFinite(ratePercent)) return null;

      const nonRateMoneyValues = moneyValues.filter((value) => Math.abs(value - ratePercent) > 0.02);
      if (nonRateMoneyValues.length < 2) return null;

      const plausiblePairs = [];
      for (let i = 0; i < nonRateMoneyValues.length; i += 1) {
        for (let j = i + 1; j < nonRateMoneyValues.length; j += 1) {
          const net = nonRateMoneyValues[i];
          const vat = nonRateMoneyValues[j];
          if (!Number.isFinite(net) || !Number.isFinite(vat) || net <= 0 || vat < 0 || vat >= net) continue;
          const expectedVat = roundToTwoDecimals(net * (ratePercent / 100));
          if (Math.abs(vat - expectedVat) <= Math.max(0.05, net * 0.005)) {
            plausiblePairs.push({ net, vat });
          }
        }
      }

      const chosenPair = plausiblePairs
        .sort((a, b) => (b.net + b.vat) - (a.net + a.vat))[0];
      if (!chosenPair) return null;

      const snappedRate = normalizeVatRateToCountry(ratePercent / 100, selectedCountry);
      const inferredRate = snappedRate ?? roundToTwoDecimals(ratePercent) / 100;
      return buildInvoiceSummary({
        gross: roundToTwoDecimals(chosenPair.net + chosenPair.vat),
        net: chosenPair.net,
        vat: chosenPair.vat,
        vatRate: inferredRate
      });
    };

    const explicitVatTableSummary = extractExplicitVatTableSummary();
    if (explicitVatTableSummary) return explicitVatTableSummary;

    const extractColumnarInvoiceSummary = () => {
      const tableLines = compactText
        .split('\n')
        .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      const summaryStart = tableLines.findIndex((line) =>
        !/\bvat\s*(code|rate)\b/i.test(line) &&
        /\b(net\s*goods|total\s*net\s*value|total\s*invoice\s*value|weee\s*charge|delivery\s*charge)\b/i.test(line)
      );
      if (summaryStart === -1) return null;

      const summaryLines = [];
      for (let index = summaryStart; index < tableLines.length && summaryLines.length < 18; index += 1) {
        const line = tableLines[index];
        if (index > summaryStart && /\b(vat\s*reg|registered|company|address|page|iso\s*\d|thank\s+you)\b/i.test(line)) {
          break;
        }
        summaryLines.push(line);
      }

      const labelForLine = (line) => {
        const lower = String(line || '').toLowerCase();
        if (/\bvat\s*(code|rate|reg|registration|no|number|id)\b/i.test(lower)) return null;
        if (/\btotal\s*invoice\s*value\b/i.test(lower)) return 'gross';
        if (/^\s*total\s*$/i.test(line) || /\bgrand\s*total\b/i.test(lower)) return 'gross';
        if (/\btotal\s*net\s*value\b/i.test(lower) || /\bnet\s*goods\s*&\s*delivery\b/i.test(lower)) return 'totalNet';
        if (/\bnet\s*goods\b/i.test(lower)) return 'net';
        if (/\bweee\s*charge\b/i.test(lower)) return 'weee';
        if (/\bdelivery\s*charge\b/i.test(lower)) return 'delivery';
        if (/^\s*vat\s*$/i.test(line) || /^\s*vat\s+[€£$]?\s*$/i.test(line)) return 'vat';
        return null;
      };

      const labels = [];
      summaryLines.forEach((line, index) => {
        const key = labelForLine(line);
        if (!key) return;
        if (labels.some((entry) => entry.key === key)) return;
        labels.push({ key, index });
      });
      if (labels.length < 3) return null;

      const valuesByKey = {};
      summaryLines.forEach((line) => {
        const key = labelForLine(line);
        if (!key) return;
        const values = parseMoneyValues(line);
        if (values.length > 0) {
          valuesByKey[key] = values[values.length - 1];
        }
      });

      const amountEntries = [];
      summaryLines.forEach((line, index) => {
        if (/\b(vat\s*rate|vat\s*code|vat\s*reg|invoice\s*(?:date|no|number)|customer\s*no|page)\b/i.test(line)) {
          return;
        }
        parseMoneyValues(line).forEach((value) => amountEntries.push({ value, index }));
      });

      const orderedAmounts = amountEntries
        .filter((entry) => entry.index >= labels[0].index)
        .map((entry) => entry.value);
      if (orderedAmounts.length >= labels.length) {
        labels.forEach((entry, index) => {
          if (!Number.isFinite(valuesByKey[entry.key])) {
            valuesByKey[entry.key] = orderedAmounts[index];
          }
        });
      }

      const resolvedNet = Number.isFinite(valuesByKey.totalNet) ? valuesByKey.totalNet : valuesByKey.net;
      const summary = buildInvoiceSummary({
        gross: valuesByKey.gross,
        net: resolvedNet,
        vat: valuesByKey.vat
      });

      if (
        summary &&
        Number.isFinite(resolvedNet) &&
        Number.isFinite(valuesByKey.vat) &&
        Math.abs(summary.gross - (resolvedNet + valuesByKey.vat)) <= 0.05
      ) {
        return summary;
      }

      return Number.isFinite(resolvedNet) && Number.isFinite(valuesByKey.vat) ? summary : null;
    };

    const columnarSummary = extractColumnarInvoiceSummary();
    if (columnarSummary) return columnarSummary;

    if (/\bvat\s*code\b/i.test(compactText) && /\bnet\s*goods\b/i.test(compactText)) {
      const tableLines = compactText
        .split('\n')
        .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      const getAmountAfterTableLabel = (labelPattern, rejectPattern = null) => {
        for (let index = 0; index < tableLines.length; index += 1) {
          const line = tableLines[index];
          if (!labelPattern.test(line)) continue;
          if (rejectPattern && rejectPattern.test(line)) continue;
          if (/\bvat\s*code\b/i.test(line) && /\bvat\s*rate\b/i.test(line) && /\bnet\s*goods\b/i.test(line)) continue;

          const inlineValues = (line.match(/\d+[.,]\d{2}/g) || [])
            .map((token) => parseAmount(token))
            .filter((value) => Number.isFinite(value));
          if (inlineValues.length) return inlineValues[inlineValues.length - 1];

          for (let offset = 1; offset <= 4 && index + offset < tableLines.length; offset += 1) {
            const nearbyLine = tableLines[index + offset];
            if (/\b(vat\s*reg|registered|company|address|page)\b/i.test(nearbyLine)) break;
            if (/^\s*vat\s*$/i.test(line) && /\b(vat\s*code|vat\s*rate|net\s*goods|total\s*net|total\s*invoice|delivery\s*charge|weee\s*charge)\b/i.test(nearbyLine)) {
              continue;
            }
            const nearbyValues = (nearbyLine.match(/\d+[.,]\d{2}/g) || [])
              .map((token) => parseAmount(token))
              .filter((value) => Number.isFinite(value));
            if (nearbyValues.length) return nearbyValues[0];
          }
        }
        return null;
      };

      const tableRatePercent = getAmountAfterTableLabel(/\bvat\s*rate\b/i);
      const tableNet = getAmountAfterTableLabel(/\bnet\s*goods\b/i);
      const tableVat = getAmountAfterTableLabel(/^\s*vat\s*$/i);
      if (
        Number.isFinite(tableRatePercent) &&
        Number.isFinite(tableNet) &&
        Number.isFinite(tableVat) &&
        tableRatePercent >= 0 &&
        tableRatePercent <= 100 &&
        tableNet > 0 &&
        tableVat >= 0
      ) {
        const snappedRate = normalizeVatRateToCountry(tableRatePercent / 100, selectedCountry);
        const inferredRate = snappedRate ?? roundToTwoDecimals(tableRatePercent) / 100;
        const gross = roundToTwoDecimals(tableNet + tableVat);

        return {
          gross,
          vatAmount: roundToTwoDecimals(tableVat),
          vatRate: inferredRate,
          vatComponents: tableVat > 0
            ? [{
                rate: inferredRate,
                amount: roundToTwoDecimals(tableVat),
                description: inferredRate > 0 ? `${(inferredRate * 100).toFixed(1)}% VAT` : 'VAT'
              }]
            : [],
          depositRefund: 0,
          forceReplaceItems: true
        };
      }

    }

    const patterns = [
      new RegExp(`\\bnet\\s*goods(?:\\s*(?:less\\s*discount|&\\s*delivery))?\\b\\D{0,40}${amountPattern}[\\s\\S]{0,160}\\bvat\\b\\D{0,40}${amountPattern}[\\s\\S]{0,100}\\btotal\\b\\D{0,40}${amountPattern}`, 'i'),
      new RegExp(`\\bsub[-\\s]?total\\b\\D{0,40}${amountPattern}[\\s\\S]{0,120}\\bvat\\b(?:\\s*\\d{1,2}(?:[.,]\\d+)?\\s*%)?\\D{0,40}${amountPattern}[\\s\\S]{0,120}\\b(?:grand\\s*total|payment\\s*due|amount\\s*due|balance\\s*due|total)\\b\\D{0,40}${amountPattern}`, 'i')
    ];

    for (const pattern of patterns) {
      const match = compactText.match(pattern);
      if (!match) continue;

      const net = parseAmount(match[1]);
      const vat = parseAmount(match[2]);
      const gross = parseAmount(match[3]);
      if (!Number.isFinite(gross) || gross <= 0) continue;
      if (!Number.isFinite(vat) || vat < 0) continue;

      const inferredRawRate = Number.isFinite(net) && net > 0 && vat > 0
        ? vat / net
        : 0;
      const inferredRate = inferredRawRate > 0
        ? normalizeVatRateToCountry(inferredRawRate, selectedCountry)
        : 0;

      return {
        gross: roundToTwoDecimals(gross),
        vatAmount: roundToTwoDecimals(vat),
        vatRate: inferredRate,
        vatComponents: vat > 0
          ? [{
              rate: inferredRate,
              amount: roundToTwoDecimals(vat),
              description: inferredRate > 0 ? `${(inferredRate * 100).toFixed(1)}% VAT` : 'VAT'
            }]
          : [],
        depositRefund: 0
      };
    }

    return null;
  };

  const extractUtilityBillSummaryFromText = (rawText = '') => {
    const text = String(rawText || '').replace(/\r/g, '\n');
    if (!text || !/\b(bill summary|total due|charges for this period|electric ireland|billing period)\b/i.test(text)) {
      return null;
    }

    const lines = text
      .split('\n')
      .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    const parseLineAmount = (line, { allowSplitCents = false } = {}) => {
      const match = String(line || '').match(/-?\d+[.,]\d{2}/);
      let parsed = match ? parseFloat(match[0].replace(',', '.')) : null;
      if (!Number.isFinite(parsed) && allowSplitCents) {
        const splitCentsMatch = String(line || '').match(/\b(\d{1,4})\s+(\d{2})\b/);
        if (splitCentsMatch) {
          parsed = parseFloat(`${splitCentsMatch[1]}.${splitCentsMatch[2]}`);
        }
      }
      if (!Number.isFinite(parsed)) return null;
      return /\b(cr|credit)\b/i.test(line) ? -Math.abs(parsed) : parsed;
    };

    const getPaymentSlipAmount = () => {
      const compactDigits = lines.join(' ').match(/\b0{4,}(\d{4})\d\b/);
      if (!compactDigits) return null;
      const cents = parseInt(compactDigits[1], 10);
      if (!Number.isFinite(cents) || cents <= 0) return null;
      return cents / 100;
    };

    const getColumnarBillSummary = () => {
      const startIndex = lines.findIndex((line) => /\bbill\s*summary\b/i.test(line));
      if (startIndex < 0) return null;

      const labels = [];
      const amounts = [];
      for (let index = startIndex + 1; index < Math.min(lines.length, startIndex + 35); index += 1) {
        const line = lines[index];
        if (/\b(your\s*account\s*number|to\s*ask\s*about|call|last\s*4\s*bills|direct\s*debit|payment\s*terms|gprn|date\s*of\s*issue)\b/i.test(line)) {
          break;
        }

        const amount = parseLineAmount(line, { allowSplitCents: false });
        if (amount !== null) {
          amounts.push(amount);
          continue;
        }

        if (/\b(your\s*last\s*bill|payments?\/?transactions?|balance\s*brought\s*forward|charges\s*for\s*this\s*period|your\s*savings|vat|total\s*due)\b/i.test(line)) {
          labels.push(line.toLowerCase());
        }
      }

      if (labels.length < 4 || amounts.length < 4) return null;

      const amountForLabel = (labelPattern) => {
        const labelIndex = labels.findIndex((label) => labelPattern.test(label));
        return labelIndex >= 0 && labelIndex < amounts.length ? Math.abs(amounts[labelIndex]) : null;
      };

      return {
        charges: amountForLabel(/\bcharges\s*for\s*this\s*period\b/),
        savings: amountForLabel(/\byour\s*savings\b/),
        vatAmount: amountForLabel(/\bvat\b/),
        totalDue: amountForLabel(/\btotal\s*due\b/)
      };
    };

    const getAmountForLabel = (labelPattern, { forcePositive = false, exactVatLine = false } = {}) => {
      for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const lower = line.toLowerCase();
        if (/vat\s*(reg|registration|no|number)/i.test(lower)) continue;
        if (exactVatLine && !/^\s*vat\b[^\d-]*-?\d*[.,]?\d*\s*$/i.test(line) && !/^\s*vat\s*$/i.test(line)) {
          continue;
        }
        if (!labelPattern.test(lower)) continue;

        let amount = parseLineAmount(line, { allowSplitCents: true });
        if (amount === null) {
          for (let offset = 1; offset <= 4 && index + offset < lines.length; offset += 1) {
            const nearbyLine = lines[index + offset];
            if (/\b(your\s*last\s*bill|payments?\/?transactions?|balance\s*brought\s*forward|account\s*number|call|phone|barcode)\b/i.test(nearbyLine)) {
              break;
            }
            amount = parseLineAmount(nearbyLine, { allowSplitCents: true });
            if (amount !== null) break;
          }
        }
        if (amount === null) continue;
        return forcePositive ? Math.abs(amount) : amount;
      }
      return null;
    };

    const columnSummary = getColumnarBillSummary();
    const totalDue = getPaymentSlipAmount() ||
      columnSummary?.totalDue ||
      getAmountForLabel(/\b(total\s*due|amount\s*due|amount\s*payable|total\s*amount)\b/, { forcePositive: true });
    const charges = columnSummary?.charges ||
      getAmountForLabel(/\bcharges\s*for\s*this(?:\s*period)?\b/, { forcePositive: true });
    const savings = columnSummary?.savings ||
      getAmountForLabel(/\byour\s*savings\b|\bsavings\b/, { forcePositive: true });
    const vatAmount = columnSummary?.vatAmount ||
      getAmountForLabel(/\bvat\b/, { forcePositive: true, exactVatLine: true });

    if (!Number.isFinite(totalDue) && !Number.isFinite(vatAmount)) return null;

    const netFromRows = Number.isFinite(charges) && charges > 0
      ? Math.max(0, charges - (Number.isFinite(savings) ? savings : 0))
      : null;
    const netFromTotal = Number.isFinite(totalDue) && totalDue > 0 && Number.isFinite(vatAmount)
      ? Math.max(0, totalDue - vatAmount)
      : null;
    const net = Number.isFinite(netFromRows) && netFromRows > 0 ? netFromRows : netFromTotal;
    const gross = Number.isFinite(totalDue) && totalDue > 0
      ? totalDue
      : (Number.isFinite(net) && Number.isFinite(vatAmount) ? net + vatAmount : null);

    if (!Number.isFinite(gross) || gross <= 0) return null;

    const vatRate = Number.isFinite(net) && net > 0 && Number.isFinite(vatAmount) && vatAmount > 0
      ? normalizeVatRateToCountry(vatAmount / net, selectedCountry)
      : 0;
    const normalizedVatAmount = Number.isFinite(vatAmount) ? roundToTwoDecimals(vatAmount) : 0;

    return {
      gross: roundToTwoDecimals(gross),
      vatAmount: normalizedVatAmount,
      vatRate,
      vatComponents: Number.isFinite(normalizedVatAmount) && vatRate !== null
        ? [{
            rate: vatRate,
            amount: normalizedVatAmount,
            description: vatRate > 0 ? `${(vatRate * 100).toFixed(1)}% VAT` : '0% VAT'
          }]
        : [],
      depositRefund: 0,
      category: 'utilities',
      forceReplaceItems: true
    };
  };

  const extractIrishVatSummaryFromText = (rawText = '') => {
    const text = String(rawText || '').replace(/\r/g, '');
    if (!text) return null;

    const patterns = [
      /(?:^|\n)\s*([A-Z])\s+(\d{1,2}(?:[.,]\d+)?)\s*%?\s+Net\s+(-?\d+[.,]\d{2})\s+Vat\s+(-?\d+[.,]\d{2})(?=\n|$)/gi,
      /(?:^|\n)\s*([A-Z])\s+(\d{1,2}(?:[.,]\d+)?)\s*%?\s+Net\s*\n\s*(-?\d+[.,]\d{2})\s+Vat\s*\n\s*(-?\d+[.,]\d{2})(?=\n|$)/gi
    ];

    const rows = [];
    const seen = new Set();

    patterns.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const code = String(match[1] || '').toUpperCase();
        const rate = parseFloat(String(match[2] || '').replace(',', '.')) / 100;
        const net = parseAmountNumber(match[3]);
        const vat = parseAmountNumber(match[4]);
        if (!code || !Number.isFinite(rate) || rate < 0 || rate > 1) continue;
        if (!Number.isFinite(net) || !Number.isFinite(vat)) continue;

        const key = `${code}:${rate.toFixed(4)}:${net.toFixed(2)}:${vat.toFixed(2)}`;
        if (seen.has(key)) continue;
        seen.add(key);

        rows.push({
          code,
          rate: roundToTwoDecimals(rate * 100) / 100,
          net: roundToTwoDecimals(net),
          vat: roundToTwoDecimals(vat)
        });
      }
    });

    const lines = text
      .split('\n')
      .map((line) => String(line || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    for (let index = 0; index < lines.length; index += 1) {
      const startMatch = lines[index].match(/^([A-Z])\s+(\d{1,2}(?:[.,]\d+)?)\s*%?\s*$/i);
      if (!startMatch) continue;

      const nearbyLines = [];
      for (let offset = 1; offset <= 8 && index + offset < lines.length; offset += 1) {
        const nearbyLine = lines[index + offset] || '';
        if (/^(vat\s*reg|weee|thank\s+you|total\s+euro|cards?|visa|mastercard)\b/i.test(nearbyLine)) {
          break;
        }
        nearbyLines.push(nearbyLine);
      }

      const numericValues = nearbyLines
        .flatMap((line) => line.match(/-?\d+[.,]\d{2}/g) || [])
        .map((token) => parseAmountNumber(token))
        .filter((value) => Number.isFinite(value));

      if (numericValues.length < 2) continue;

      const code = String(startMatch[1] || '').toUpperCase();
      const rate = parseFloat(String(startMatch[2] || '').replace(',', '.')) / 100;
      const net = numericValues[0];
      const vatCandidate = numericValues.find((value, valueIndex) =>
        valueIndex > 0 &&
        Math.abs(value) <= Math.max(0.01, Math.abs(net) * 0.35)
      );
      const vat = Number.isFinite(vatCandidate) ? vatCandidate : numericValues[1];
      if (!code || !Number.isFinite(rate) || rate < 0 || rate > 1) continue;

      const key = `${code}:${rate.toFixed(4)}:${net.toFixed(2)}:${vat.toFixed(2)}`;
      if (seen.has(key)) continue;
      seen.add(key);

      rows.push({
        code,
        rate: roundToTwoDecimals(rate * 100) / 100,
        net: roundToTwoDecimals(net),
        vat: roundToTwoDecimals(vat)
      });
    }

    if (rows.length === 0) return null;

    const gross = roundToTwoDecimals(rows.reduce((sum, row) => sum + row.net + row.vat, 0));
    const vatAmount = roundToTwoDecimals(rows.reduce((sum, row) => sum + Math.abs(row.vat), 0));
    const vatRate = rows.reduce((highest, row) => (row.rate > highest ? row.rate : highest), 0);
    const depositRefund = roundToTwoDecimals(
      Math.abs(rows.filter((row) => row.net < 0).reduce((sum, row) => sum + row.net, 0))
    );

    const vatComponents = rows.map((row) => ({
      rate: row.rate,
      amount: roundToTwoDecimals(Math.abs(row.vat)),
      description:
        row.rate === 0 && row.net < 0
          ? 'Deposit Return (0%)'
          : row.rate === 0
            ? 'Zero Rate (0%)'
            : `${(row.rate * 100).toFixed(1)}% VAT`
    }));

    if (!Number.isFinite(gross) || gross <= 0) return null;

    return {
      gross,
      vatAmount,
      vatRate,
      vatComponents,
      depositRefund
    };
  };

  const applyReceiptTextCorrections = (data = {}, rawText = '') => {
    const sourceText = rawText || data?.rawText || data?.text || data?.fullText || '';
    const looksLikeSupplierInvoice = /\b(sales\s+invoice|invoice\s*(?:no|#|number)|bill\s*to|payment\s*due|vat\s*code|vat\s*rate|subtotal|total\s+invoice\s+value|net\s+goods)\b/i.test(sourceText);
    const invoiceAccountingSignal = /\b(accounting|accountancy|accountant|accountants|bookkeeping|bookkeeper|payroll)\b/i.test(sourceText);
    const utilitySummary = extractUtilityBillSummaryFromText(sourceText);
    const invoiceSummary = extractInvoiceSummaryVatFromText(sourceText);
    const summary = utilitySummary ||
      invoiceSummary ||
      (looksLikeSupplierInvoice ? null : extractIrishVatSummaryFromText(sourceText));
    if (!summary) return data;

    console.log('Applied receipt text corrections from receipt summary:', summary);

    const corrected = {
      ...data,
      amount: summary.gross,
      category: invoiceAccountingSignal ? 'accounting' : (summary.category || data.category),
      suggestedCategoryName: invoiceAccountingSignal ? '' : data.suggestedCategoryName,
      vatAmount: summary.vatAmount,
      vatRate: summary.vatRate,
      vatComponents: summary.vatComponents
    };

    const currentItems = Array.isArray(corrected.items) ? corrected.items : [];
    const hasMeaningfulCurrentItems = currentItems.some((item) =>
      Number.isFinite(Number(item?.amount)) &&
      Math.abs(Number(item?.amount)) > 0.0001 &&
      !isGenericPlaceholderLineItem(item, corrected.vendor)
    );
    const shouldReplaceItems =
      (summary.forceReplaceItems && !hasMeaningfulCurrentItems) ||
      currentItems.length === 0 ||
      (currentItems.length === 1 && isGenericPlaceholderLineItem(currentItems[0], corrected.vendor));

    if (shouldReplaceItems) {
      const positivePurchaseAmount = summary.depositRefund > 0
        ? roundToTwoDecimals(summary.gross + summary.depositRefund)
        : summary.gross;
      const summaryIsZeroRated = summary.vatComponents.length > 0 &&
        summary.vatComponents.every((component) => Number(component?.rate) === 0 && Number(component?.amount) === 0);

      corrected.items = [
        {
          description: `${corrected.vendor || 'Receipt'} purchase`,
          quantity: 1,
          unitPrice: positivePurchaseAmount,
          amount: positivePurchaseAmount,
          category: normalizeCategoryId(corrected.category || 'misc'),
          vatRate: summary.vatRate ?? (summaryIsZeroRated ? 0 : undefined),
          vatAmount: summary.vatAmount ?? (summaryIsZeroRated ? 0 : undefined),
          vatReclaimable: summaryIsZeroRated ? false : undefined
        }
      ];

      if (summary.depositRefund > 0) {
        corrected.items.push({
          description: 'Bottle Deposit Return',
          quantity: 1,
          unitPrice: -summary.depositRefund,
          amount: -summary.depositRefund,
          category: 'misc',
          vatRate: 0,
          vatAmount: 0
        });
      }
    } else if (summary.vatComponents.length > 0 &&
      summary.vatComponents.every((component) => Number(component?.rate) === 0 && Number(component?.amount) === 0)) {
      corrected.items = currentItems.map((item) => ({
        ...item,
        vatRate: 0,
        vatAmount: 0,
        vatReclaimable: false
      }));
    }

    return corrected;
  };

  const DEFAULT_EXPENSE_CATEGORY_IDS = new Set(
    (DEFAULT_CATEGORIES.expenses || []).map((category) => normalizeCategoryId(category.id))
  );
  const CATEGORY_MATCH_STOP_WORDS = new Set([
    'and',
    'for',
    'the',
    'with',
    'from',
    'cost',
    'costs',
    'service',
    'services',
    'expense',
    'expenses',
    'materials',
    'material',
    'supplies',
    'supply',
    'misc',
    'miscellaneous',
    'other',
    'category',
    'purchase',
    'purchases',
    'payment',
    'payments',
    'transaction',
    'transactions',
    'receipt',
    'receipts',
    'item',
    'items',
    'order',
    'orders',
    'sale',
    'sales'
  ]);
  const GENERIC_AUTO_ASSIGN_CATEGORY_IDS = new Set(['', 'supplies', 'misc', 'other']);
  const NON_RECLAIMABLE_FOOD_CATEGORY_IDS = new Set(['meals', 'food', 'food_and_drink']);

  const normalizeMatchText = (value) =>
    String(value || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const stemMatchToken = (token) => {
    if (token.endsWith('ies') && token.length > 4) {
      return `${token.slice(0, -3)}y`;
    }
    if (token.endsWith('s') && token.length > 3 && !token.endsWith('ss')) {
      return token.slice(0, -1);
    }
    return token;
  };

  const tokenizeForCategoryMatch = (value) =>
    normalizeMatchText(value)
      .split(' ')
      .map((token) => stemMatchToken(token))
      .filter((token) => token.length >= 3 && !CATEGORY_MATCH_STOP_WORDS.has(token));

  const buildCategoryMatchers = () => {
    const expenseCategories = Array.isArray(categories?.expenses) ? categories.expenses : [];

    return expenseCategories
      .map((category) => {
        const rawCategoryId = normalizeCategoryId(category?.id || '');
        if (!rawCategoryId) return null;

        const rawCategoryName = String(category?.name || rawCategoryId).trim();
        const normalizedName = normalizeMatchText(rawCategoryName);
        const tokens = Array.from(
          new Set([
            ...tokenizeForCategoryMatch(rawCategoryName),
            ...tokenizeForCategoryMatch(rawCategoryId)
          ])
        );

        if (!normalizedName && tokens.length === 0) {
          return null;
        }

        return {
          id: rawCategoryId,
          normalizedName,
          tokens,
          isCustomCategory: !DEFAULT_EXPENSE_CATEGORY_IDS.has(rawCategoryId),
          isGenericCategory: GENERIC_AUTO_ASSIGN_CATEGORY_IDS.has(rawCategoryId)
        };
      })
      .filter(Boolean);
  };

  const matchLineItemToCategory = (description, categoryMatchers = []) => {
    const normalizedDescription = normalizeMatchText(description);
    if (!normalizedDescription) return null;

    const descriptionTokens = new Set(tokenizeForCategoryMatch(description));
    let bestMatch = null;

    categoryMatchers.forEach((matcher) => {
      let matchedTokens = 0;
      matcher.tokens.forEach((token) => {
        if (descriptionTokens.has(token) || (token.length >= 5 && normalizedDescription.includes(token))) {
          matchedTokens += 1;
        }
      });

      let score = matchedTokens * 2;
      if (matcher.tokens.length > 1 && matchedTokens === matcher.tokens.length) {
        score += 2;
      }
      if (matcher.normalizedName && matcher.normalizedName.length >= 3 && normalizedDescription.includes(matcher.normalizedName)) {
        score += matcher.tokens.length + 2;
      }
      if (score <= 0) return;

      const candidate = {
        id: matcher.id,
        score,
        matchedTokens,
        isCustomCategory: matcher.isCustomCategory,
        isGenericCategory: matcher.isGenericCategory
      };

      if (
        !bestMatch ||
        candidate.score > bestMatch.score ||
        (candidate.score === bestMatch.score && candidate.isCustomCategory && !bestMatch.isCustomCategory) ||
        (candidate.score === bestMatch.score && !candidate.isGenericCategory && bestMatch.isGenericCategory) ||
        (candidate.score === bestMatch.score && candidate.matchedTokens > bestMatch.matchedTokens)
      ) {
        bestMatch = candidate;
      }
    });

    return bestMatch?.id || null;
  };

  const isNonReclaimableFoodCategory = (category) =>
    NON_RECLAIMABLE_FOOD_CATEGORY_IDS.has(normalizeCategoryId(category || ''));

  const getDominantLineItemCategory = (items = [], fallbackCategory = 'supplies') => {
    const normalizedFallbackCategory = normalizeCategoryId(fallbackCategory || 'supplies');
    const totalsByCategory = new Map();

    (items || []).forEach((item) => {
      const category = normalizeCategoryId(item?.category || '');
      if (!category || GENERIC_AUTO_ASSIGN_CATEGORY_IDS.has(category)) return;
      if (category === normalizedFallbackCategory && GENERIC_AUTO_ASSIGN_CATEGORY_IDS.has(normalizedFallbackCategory)) return;
      const amount = Math.abs(parseAmountNumber(item?.amount ?? item?.total) || 0) || 1;
      totalsByCategory.set(category, (totalsByCategory.get(category) || 0) + amount);
    });

    let bestCategory = '';
    let bestTotal = 0;
    totalsByCategory.forEach((total, category) => {
      if (total > bestTotal) {
        bestCategory = category;
        bestTotal = total;
      }
    });

    return bestCategory;
  };

  const matchReceiptTextToCategory = (data = {}, items = [], rawText = '', categoryMatchers = []) => {
    const combinedText = [
      data.vendor,
      data.description,
      data.notes,
      rawText || data.rawText || data.text || data.fullText,
      ...(Array.isArray(items)
        ? items.map((item) => `${item?.description || ''} ${item?.name || ''} ${item?.item || ''}`)
        : [])
    ].join(' ');

    return matchLineItemToCategory(combinedText, categoryMatchers);
  };

  const autoAssignLineItemCategories = (items = [], fallbackCategory = 'supplies', categoryMatchers = buildCategoryMatchers()) => {
    if (!Array.isArray(items) || items.length === 0) return items;

    if (categoryMatchers.length === 0) return items;

    const normalizedFallbackCategory = normalizeCategoryId(fallbackCategory || 'supplies');

    return items.map((item) => {
      const currentCategory = normalizeCategoryId(item?.category || normalizedFallbackCategory);
      const shouldAutoAssign =
        !item?.category ||
        currentCategory === normalizedFallbackCategory ||
        GENERIC_AUTO_ASSIGN_CATEGORY_IDS.has(currentCategory);

      if (!shouldAutoAssign) {
        return {
          ...item,
          category: currentCategory
        };
      }

      const description = String(item?.description ?? item?.name ?? item?.item ?? '').trim();
      const matchedCategory = matchLineItemToCategory(description, categoryMatchers);
      const inferredCategory = guardAutoDetectedReceiptExpenseCategory({
        category: matchedCategory || currentCategory || normalizedFallbackCategory,
        amount: item?.amount ?? item?.total ?? 0,
        vendor: description,
        items: [{ ...item, description, category: matchedCategory || currentCategory }],
        rawText: description
      });

      return {
        ...item,
        category: normalizeCategoryId(inferredCategory || matchedCategory || currentCategory || normalizedFallbackCategory)
      };
    });
  };

  const hasNonEmptyValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';
  const isGenericPlaceholderLineItem = (item = {}, vendor = '') => {
    const description = String(item?.description || '').trim().toLowerCase();
    if (!description) return false;

    const normalizedVendor = String(vendor || '').trim().toLowerCase();
    if (normalizedVendor && description === `${normalizedVendor} purchase`) {
      return true;
    }

    return /\b(receipt|purchase|transaction|payment)\b/.test(description);
  };

  const sanitizeReceiptData = (data = {}) => {
    const initialCategory = normalizeCategoryId(data.category || 'supplies');
    const normalizedAmountRaw = parseAmountNumber(data.amount);
    const normalizedAmount = normalizedAmountRaw === null ? 0 : roundToTwoDecimals(normalizedAmountRaw);
    const comparableAmount = Math.abs(normalizedAmount);

    let normalizedVatRate = parseVatRateNumber(data.vatRate);
    let normalizedVatAmount = parseAmountNumber(data.vatAmount);
    if (normalizedVatAmount !== null) {
      normalizedVatAmount = roundToTwoDecimals(normalizedVatAmount);
    }

    if (comparableAmount > 0) {
      if (normalizedVatAmount !== null && Math.abs(normalizedVatAmount) > comparableAmount) {
        normalizedVatAmount = null;
      }
    }

    let normalizedVatComponents = Array.isArray(data.vatComponents)
      ? data.vatComponents
          .map((component) => {
            const componentRate = parseVatRateNumber(component?.rate);
            let componentAmount = parseAmountNumber(component?.amount);
            if (componentAmount !== null) {
              componentAmount = roundToTwoDecimals(componentAmount);
            }

            if (comparableAmount > 0 && componentAmount !== null && Math.abs(componentAmount) > comparableAmount) {
              componentAmount = null;
            }

            if (componentRate === null && componentAmount === null) {
              return null;
            }

            return {
              rate: componentRate,
              amount: componentAmount,
              description:
                component?.description ||
                (componentRate !== null
                  ? `${(componentRate * 100).toFixed(1)}% VAT`
                  : 'VAT')
            };
          })
          .filter(Boolean)
      : [];

    if (
      normalizedVatComponents.length === 0 &&
      normalizedVatRate !== null &&
      normalizedVatAmount !== null &&
      (normalizedVatRate > 0 || normalizedVatAmount > 0)
    ) {
      normalizedVatComponents = [
        {
          rate: normalizedVatRate,
          amount: normalizedVatAmount,
          description: `${(normalizedVatRate * 100).toFixed(1)}% VAT`
        }
      ];
    }

    let normalizedItems = Array.isArray(data.items)
      ? data.items
          .map((item, index) => {
            const rawDescription = String(item?.description ?? item?.name ?? item?.item ?? '').trim();
            const parsedPackDetails = parseLineItemPackDetails(rawDescription);
            const itemQtyRaw = parseAmountNumber(item?.quantity);
            const itemAmountRaw = parseAmountNumber(item?.amount ?? item?.total);
            const itemUnitPriceRaw = parseAmountNumber(item?.unitPrice);
            const itemUnitsPerPackRaw = parseAmountNumber(item?.unitsPerPack);
            const itemPackSizeRaw = parseAmountNumber(item?.packSize);
            const hasItemUnitsPerPack = itemUnitsPerPackRaw !== null && itemUnitsPerPackRaw > 0;
            const hasItemPackSize = itemPackSizeRaw !== null && itemPackSizeRaw > 0;
            const fallbackUnitsPerPack = parsedPackDetails.unitsPerPack ?? '';
            const resolvedUnitsPerPack = hasItemUnitsPerPack
              ? itemUnitsPerPackRaw
              : (hasItemPackSize ? itemPackSizeRaw : fallbackUnitsPerPack);
            const resolvedUnitType = hasNonEmptyValue(item?.unitType)
              ? item.unitType
              : (parsedPackDetails.unitType || '');
            const resolvedPackType = hasNonEmptyValue(item?.packType)
              ? item.packType
              : (parsedPackDetails.packType || '');
            const resolvedBaseDescription = hasNonEmptyValue(item?.baseDescription)
              ? String(item.baseDescription).trim()
              : (parsedPackDetails.baseDescription || rawDescription || `Item ${index + 1}`);

            let itemAmount = itemAmountRaw;
            let itemUnitPrice = itemUnitPriceRaw;

            if (itemAmount === null && itemUnitPrice !== null && itemQtyRaw !== null && itemQtyRaw !== 0) {
              itemAmount = roundToTwoDecimals(itemUnitPrice * itemQtyRaw);
            } else if (itemAmount === null && itemUnitPrice !== null) {
              itemAmount = roundToTwoDecimals(itemUnitPrice);
            }

            if (itemUnitPrice === null && itemAmount !== null && itemQtyRaw !== null && itemQtyRaw !== 0) {
              itemUnitPrice = roundToTwoDecimals(itemAmount / itemQtyRaw);
            }

            return {
              ...item,
              description: rawDescription || `Item ${index + 1}`,
              baseDescription: resolvedBaseDescription,
              quantity: itemQtyRaw === null ? item?.quantity ?? 1 : itemQtyRaw,
              unitPrice: itemUnitPrice,
              amount: itemAmount,
              unitsPerPack: resolvedUnitsPerPack,
              packSize:
                hasItemPackSize
                  ? itemPackSizeRaw
                  : ((resolvedUnitType === 'ml' || resolvedUnitType === 'g') && resolvedUnitsPerPack !== ''
                    ? resolvedUnitsPerPack
                    : (item?.packSize ?? '')),
              packType: resolvedPackType,
              unitType: resolvedUnitType
            };
          })
          .filter((item) => item.amount !== null && Math.abs(item.amount) > 0.0001)
      : [];

    if (comparableAmount > 0 && normalizedItems.length === 0) {
      normalizedItems = [
        {
          description: `${data.vendor || 'Receipt'} purchase`,
          quantity: 1,
          unitPrice: normalizedAmount,
          amount: normalizedAmount,
          category: initialCategory
        }
      ];
    }

    const totalItemsAmount = normalizedItems.reduce((sum, item) => sum + Math.abs(item.amount || 0), 0);
    if (
      comparableAmount > 0 &&
      normalizedItems.length > 0 &&
      totalItemsAmount > comparableAmount * 5
    ) {
      const fallbackItem = normalizedItems[0] || {};
      normalizedItems = [
        {
          ...fallbackItem,
          description: fallbackItem.description || `${data.vendor || 'Receipt'} purchase`,
          quantity: 1,
          unitPrice: normalizedAmount,
          amount: normalizedAmount,
          category: normalizeCategoryId(fallbackItem.category || initialCategory)
        }
      ];
    }

    const categoryMatchers = buildCategoryMatchers();
    normalizedItems = autoAssignLineItemCategories(
      normalizedItems,
      initialCategory,
      categoryMatchers
    );

    const rawTextForCategory = data.rawText || data.text || data.fullText || '';
    const matchedReceiptCategory = matchReceiptTextToCategory(
      data,
      normalizedItems,
      rawTextForCategory,
      categoryMatchers
    );
    const dominantLineItemCategory = getDominantLineItemCategory(normalizedItems, initialCategory);
    const invoiceAccountingSignal = /\b(accounting|accountancy|accountant|accountants|bookkeeping|bookkeeper|payroll)\b/i.test(rawTextForCategory);
    const categoryCandidate = invoiceAccountingSignal
      ? 'accounting'
      : (matchedReceiptCategory || dominantLineItemCategory || initialCategory);

    const guardedCategory = guardAutoDetectedReceiptExpenseCategory({
      category: categoryCandidate,
      amount: normalizedAmount,
      vendor: data.vendor || '',
      items: normalizedItems,
      rawText: rawTextForCategory
    }) || categoryCandidate || initialCategory;

    if (isNonReclaimableFoodCategory(guardedCategory)) {
      normalizedVatRate = 0;
      normalizedVatAmount = 0;
      normalizedVatComponents = [];
      normalizedItems = normalizedItems.map((item) => ({
        ...item,
        category: item.category || 'meals',
        vatRate: 0,
        vatAmount: 0,
        vatReclaimable: false
      }));
    }

    // Keep the single fallback line-item category aligned with the receipt category.
    if (
      normalizedItems.length === 1 &&
      isGenericPlaceholderLineItem(normalizedItems[0], data.vendor) &&
      guardedCategory
    ) {
      normalizedItems = [
        {
          ...normalizedItems[0],
          category: guardedCategory
        }
      ];
    }

    return {
      ...data,
      amount: normalizedAmount,
      category: guardedCategory,
      netAmount: isNonReclaimableFoodCategory(guardedCategory) ? normalizedAmount : data.netAmount,
      vatRate: normalizedVatRate,
      vatAmount: normalizedVatAmount,
      vatComponents: normalizedVatComponents,
      items: normalizedItems
    };
  };
  // Get receipt scanner context
  const { settings } = useReceiptScanner();

  // Get VAT settings from context
  const contextVatSettings = useContext(VatContext);
  const selectedCountry = getSelectedCountry();
  const taxName = getCountryTerminology(selectedCountry.id).taxName;
  const vatProfile = getCountryVatRateProfile(selectedCountry);
  const defaultCurrencySymbol = getCountryCurrencySymbol(selectedCountry);
  const defaultVatRate = Number.isFinite(vatProfile.standardRate) ? vatProfile.standardRate : 0;
  const defaultVatRateString = defaultVatRate.toFixed(2);
  const defaultVatRateLabel = `${(defaultVatRate * 100).toFixed(0)}% ${taxName}`;
  
  // Initialize with default values that will be overridden
  const [vatSettings, setVatSettings] = useState({ 
    enabled: false,
    isRegistered: false
  });
  
  // Effect to load VAT settings from localStorage as fallback
  useEffect(() => {
    // First try to use the context if available
    if (contextVatSettings) {
      // Check if the context has vatSettings directly or as a property
      if (typeof contextVatSettings === 'object') {
        if (contextVatSettings.enabled !== undefined) {
          // Direct vatSettings object
          console.log('ReceiptScanner: Using direct VAT settings from context:', contextVatSettings);
          setVatSettings(contextVatSettings);
          return;
        } else if (contextVatSettings.vatSettings && typeof contextVatSettings.vatSettings === 'object') {
          // Nested vatSettings object
          console.log('ReceiptScanner: Using nested VAT settings from context:', contextVatSettings.vatSettings);
          setVatSettings(contextVatSettings.vatSettings);
          return;
        }
      }
    }
    
    // Otherwise try to load from localStorage as fallback
    try {
      const savedSettings = localStorage.getItem('vat_settings') || localStorage.getItem('vatSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        console.log('ReceiptScanner: Using VAT settings from localStorage:', parsedSettings);
        setVatSettings(parsedSettings);
      } else {
        console.log('ReceiptScanner: No VAT settings found in localStorage, using defaults');
      }
    } catch (e) {
      console.error('Error loading VAT settings from localStorage:', e);
    }
  }, [contextVatSettings]);
  
  // Debug log the current VAT settings
  useEffect(() => {
    console.log('ReceiptScanner: Current VAT settings:', vatSettings);
  }, [vatSettings]);
  
  // Initial state for receipt metadata
  const getInitialMetadata = () => ({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    vendor: '',
    category: '',
    suggestedCategoryName: '',
    vatAmount: '',
    vatRate: '',
    vatComponents: [], // For multiple VAT rates
    items: [], // Line items
    currency: defaultCurrencySymbol,
    countryId: selectedCountry.id,
    notes: '',
    clientId: '',
    clientName: '',
    clientAllocations: [],
    paymentStatus: 'paid',
    amountPaid: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: paymentMethods[0]?.id || 'card',
    supplierId: '',
    supplierName: '',
    rctSubcontractorId: '',
    rctSubcontractorName: '',
    rctDeductionRate: 0.2,
    rctReverseCharge: false,
    allocateToClient: false,
    jobReference: '',
    billableToClient: false,
  });

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  const [scannedReceipt, setScannedReceipt] = useState(null);
  const [bulkReceiptQueue, setBulkReceiptQueue] = useState([]);
  const [bulkReceiptIndex, setBulkReceiptIndex] = useState(0);
  const [receiptMetadata, setReceiptMetadata] = useState(getInitialMetadata());
  const [activeTab, setActiveTab] = useState('details'); // For mobile tabs: 'details', 'items', 'vat'
  const [renderTrigger, setRenderTrigger] = useState(Date.now()); // For forcing re-renders
  const [forceUpdate, setForceUpdate] = useState(0); // Additional force update trigger
  const [, setFormLocked] = useState(false); // Track lock lifecycle for OCR hydration
  const [isImportingFromDrive, setIsImportingFromDrive] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const initialDriveImportStartedRef = useRef(false);
  const handleDriveImportRef = useRef(null);

  const getPersistedReceiptId = useCallback((receiptRecord) => {
    const rawId = receiptRecord?.id;
    if (!rawId || typeof rawId !== 'string') return null;
    const normalized = rawId.trim();
    if (!normalized || normalized.startsWith('receipt-')) return null;
    return normalized;
  }, []);

  const notifyReceiptAbandoned = useCallback((receiptRecord = scannedReceipt) => {
    const persistedId = getPersistedReceiptId(receiptRecord);
    if (!persistedId || typeof onReceiptAbandoned !== 'function') return;
    onReceiptAbandoned(persistedId);
  }, [getPersistedReceiptId, onReceiptAbandoned, scannedReceipt]);

  const notifyReceiptCommitted = useCallback((receiptRecord = scannedReceipt) => {
    const persistedId = getPersistedReceiptId(receiptRecord);
    if (!persistedId || typeof onReceiptCommitted !== 'function') return;
    onReceiptCommitted(persistedId);
  }, [getPersistedReceiptId, onReceiptCommitted, scannedReceipt]);

  useEffect(() => {
    return () => {
      notifyReceiptAbandoned(scannedReceipt);
    };
  }, [notifyReceiptAbandoned, scannedReceipt]);
  
  // Function to manually populate fields after state update
  const manuallyPopulateFields = (data) => {
    console.log('Manually populating fields with:', data);
    
    try {
      // Map the category if needed
      const normalizedCategory = normalizeCategoryId(
        categoryMap[data.category?.toLowerCase?.()] || data.category || 'misc'
      );
      const normalizedAmount = normalizeAmount(data.amount);
      
      // First update React state directly
      handleMetadataChange('date', data.date);
      handleMetadataChange('amount', normalizedAmount);
      handleMetadataChange('vendor', data.vendor || 'Unknown Vendor');
      handleMetadataChange('category', normalizedCategory);
      
      console.log('React state updated with:', {
        date: data.date,
        amount: normalizedAmount,
        vendor: data.vendor || 'Unknown Vendor',
        category: normalizedCategory
      });
      
      // Then force the DOM field values and lock them to prevent race conditions
      lockFormValues({
        date: data.date,
        amount: data.amount,
        vendor: data.vendor || 'Unknown Vendor',
        category: normalizedCategory
      });
      
      console.log('All fields manually updated');
    } catch (error) {
      console.error('Error manually populating fields:', error);
    }
  };
  
  // Debug logs for VAT data changes
  useEffect(() => {
    console.log('vatComponents changed:', receiptMetadata.vatComponents);
  }, [receiptMetadata.vatComponents]);

  useEffect(() => {
    console.log('vatAmount changed:', receiptMetadata.vatAmount);
  }, [receiptMetadata.vatAmount]);

  useEffect(() => {
    console.log('vatRate changed:', receiptMetadata.vatRate);
  }, [receiptMetadata.vatRate]);

  // Check if user has admin privileges
  const isAdmin = userRole === 'admin' || userRole === 'owner';
  
  /**
   * Handle metadata field changes
   */
  const handleMetadataChange = (field, value, index = null) => {
    console.log(`Updating metadata field: ${field} with value:`, value);
    let normalizedValue = value;
    if (field === 'category') {
      normalizedValue = normalizeCategoryId(value);
    } else if (field === 'amount') {
      // Keep raw user input to allow edits/deletes; normalize downstream when saving
      normalizedValue = value;
    }
    
    // Special handling for category field to prevent infinite loops
    if (field === 'category') {
      // Check if we're trying to set the same value (prevents loops)
      if (receiptMetadata.category === normalizedValue) {
        console.log(`Category already set to "${normalizedValue}", skipping update`);
        return;
      }
      console.log(`✅ Updating category to "${normalizedValue}"`);
    }
    
    // Add specific debug for vendor
    if (field === 'vendor') {
      console.log(`Setting vendor field to "${value}"`);
    }
    
    setReceiptMetadata(prev => {
      // Skip update if value hasn't changed (prevents loops)
      if (prev[field] === normalizedValue && index === null) {
        return prev;
      }
      
      if (field === 'vatComponents' && index !== null) {
        // Updating a VAT component
        const updatedComponents = [...(prev.vatComponents || [])];
        updatedComponents[index] = normalizedValue; // value is the entire updated component
        return { ...prev, vatComponents: updatedComponents };
      } else if (index !== null && prev.items) {
        // Updating an item in the items array
        const updatedItems = [...prev.items];
        updatedItems[index] = { ...updatedItems[index], [field]: normalizedValue };
        return { ...prev, items: updatedItems };
      } else {
        if (field === 'category' && Array.isArray(prev.items) && prev.items.length > 0) {
          const previousCategory = normalizeCategoryId(prev.category || '');
          const scannerFallbackCategory = normalizeCategoryId(isIncomeUpload ? categoryOptions[0]?.id : expenseCategoryOptions[0]?.id || 'supplies');
          const updatedItems = prev.items.map((item) => {
            const itemCategory = normalizeCategoryId(item?.category || '');
            const shouldSyncCategory =
              !itemCategory ||
              itemCategory === previousCategory ||
              itemCategory === scannerFallbackCategory;

            return shouldSyncCategory
              ? { ...item, category: normalizedValue }
              : item;
          });
          return { ...prev, [field]: normalizedValue, items: updatedItems };
        }
        // Updating a top-level field
        return { ...prev, [field]: normalizedValue };
      }
    });
    
    // Use a debounced re-render to prevent rapid updates
    clearTimeout(window._renderTimeout);
    window._renderTimeout = setTimeout(() => {
      setRenderTrigger(Date.now());
    }, 50);
  };
  
  // Custom category mapping to normalize OCR/vendor labels
  const categoryMap = {
    'office supplies': 'office',
    'officesupplies': 'office',
    'travel': 'travel_expenses',
    'transport': 'travel_expenses',
    'airfare': 'travel_expenses',
    'accommodation': 'travel_expenses',
    'circle k': 'vehicle_fuel',
    'diesel': 'vehicle_fuel',
    'petrol': 'vehicle_fuel',
    'fuel': 'vehicle_fuel',
    'vehicle fuel': 'vehicle_fuel',
    'car fuel': 'vehicle_fuel',
    'car service': 'vehicle_repairs_licensing',
    'vehicle service': 'vehicle_repairs_licensing',
    'car repair': 'vehicle_repairs_licensing',
    'vehicle repair': 'vehicle_repairs_licensing',
    'car tax': 'vehicle_repairs_licensing',
    'motor tax': 'vehicle_repairs_licensing',
    'road tax': 'vehicle_repairs_licensing',
    'vehicle licensing': 'vehicle_repairs_licensing',
    'vehicle licence': 'vehicle_repairs_licensing',
    'vehicle license': 'vehicle_repairs_licensing'
  };
  
  // Debug the category map
  console.log('Category map:', categoryMap);
  
  /**
   * Handle file selection
   */
  const handleFileSelect = (event) => {
    console.log('handleFileSelect called');
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    // Validate file type
    if (files.some((file) => !VALID_RECEIPT_FILE_TYPES.includes(file.type))) {
      onError('Invalid file type. Please upload a JPG, PNG, HEIC or PDF file.');
      return;
    }

    if (files.length > 1) {
      uploadAndScanReceiptBatch(files);
      return;
    }
    
    // Upload and scan the receipt
    uploadAndScanReceipt(files[0]);
  };

  const scanImportedDriveFiles = async (files) => {
    if (files.length > 1) {
      await uploadAndScanReceiptBatch(files);
    } else if (files.length === 1) {
      await uploadAndScanReceipt(files[0]);
    }
  };

  const handleQuickAddClient = async () => {
    if (typeof onAddClient !== 'function') return;

    const defaultName = String(receiptMetadata.clientName || receiptMetadata.vendor || '').trim();
    const enteredName = window.prompt('Client name', defaultName);
    const name = String(enteredName || '').trim();
    if (!name) return;

    const existingClient = clients.find((client) =>
      getClientOptionLabel(client).trim().toLowerCase() === name.toLowerCase()
    );
    if (existingClient) {
      if (isIncomeUpload) {
        handleMetadataChange('clientId', existingClient.id);
        handleMetadataChange('clientName', getClientOptionLabel(existingClient));
        handleMetadataChange('clientAllocations', []);
      } else {
        const allocationIds = [
          ...getExpenseAllocationIds(receiptMetadata),
          existingClient.id
        ];
        const clientAllocations = buildClientAllocationsFromIds(allocationIds);
        const primaryAllocation = clientAllocations[0];
        handleMetadataChange('clientId', primaryAllocation?.clientId || '');
        handleMetadataChange('clientName', primaryAllocation?.clientName || '');
        handleMetadataChange('clientAllocations', clientAllocations);
        handleMetadataChange('allocateToClient', clientAllocations.length > 0);
      }
      return;
    }

    try {
      const createdClient = await onAddClient({ name });
      const clientId = createdClient?.id || '';
      const clientName = createdClient ? getClientOptionLabel(createdClient) : name;
      if (isIncomeUpload && clientId) {
        handleMetadataChange('clientId', clientId);
        handleMetadataChange('clientName', clientName);
        handleMetadataChange('clientAllocations', []);
      } else if (!isIncomeUpload && clientId) {
        const allocationIds = [
          ...getExpenseAllocationIds(receiptMetadata),
          clientId
        ];
        const clientAllocations = buildClientAllocationsFromIds(allocationIds);
        const primaryAllocation = clientAllocations[0];
        handleMetadataChange('clientId', primaryAllocation?.clientId || '');
        handleMetadataChange('clientName', primaryAllocation?.clientName || '');
        handleMetadataChange('clientAllocations', clientAllocations);
        handleMetadataChange('allocateToClient', clientAllocations.length > 0);
      } else {
        handleMetadataChange('clientName', clientName);
      }
    } catch (error) {
      console.error('Failed to add client from receipt:', error);
      onError(error?.message || 'Could not add client.');
    }
  };

  const handleQuickAddSupplier = async () => {
    if (typeof onAddSupplier !== 'function') return;

    const defaultName = String(receiptMetadata.supplierName || receiptMetadata.vendor || '').trim();
    const enteredName = window.prompt('Supplier name', defaultName);
    const name = String(enteredName || '').trim();
    if (!name) return;

    const existingSupplier = supplierOptions.find((supplier) =>
      String(supplier?.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (existingSupplier) {
      handleMetadataChange('supplierId', existingSupplier.id);
      handleMetadataChange('supplierName', existingSupplier.name);
      handleMetadataChange('vendor', existingSupplier.name);
      return;
    }

    try {
      const createdSupplier = await onAddSupplier({
        name,
        supplierType: 'standard',
        rctDeductionRate: 0
      });
      const supplierId = createdSupplier?.id || createdSupplier || '';
      if (supplierId) {
        handleMetadataChange('supplierId', supplierId);
      }
      handleMetadataChange('supplierName', name);
      handleMetadataChange('vendor', name);
    } catch (error) {
      console.error('Failed to add supplier from receipt:', error);
      onError(error?.message || 'Could not add supplier.');
    }
  };

  const handleDriveImport = async () => {
    if (isUploading || isImportingFromDrive) return;

    setIsImportingFromDrive(true);
    setProcessingStatus('Opening Google Drive...');

    try {
      const pickedFiles = await openDriveReceiptPicker({ allowMultiple: true });
      if (!pickedFiles.length) {
        setProcessingStatus('');
        return;
      }

      setIsUploading(true);
      setUploadProgress(5);
      setProcessingStatus(`Downloading ${pickedFiles.length} file${pickedFiles.length === 1 ? '' : 's'} from Google Drive...`);

      const files = [];
      for (let index = 0; index < pickedFiles.length; index += 1) {
        setUploadProgress(Math.round((index / pickedFiles.length) * 25) + 5);
        const file = await downloadDriveFileAsFile(pickedFiles[index]);
        files.push(file);
      }

      if (files.some((file) => !VALID_RECEIPT_FILE_TYPES.includes(file.type))) {
        onError('One or more selected Drive files are not supported. Please choose JPG, PNG, HEIC or PDF files.');
        setIsUploading(false);
        setUploadProgress(0);
        setProcessingStatus('');
        return;
      }

      await scanImportedDriveFiles(files);
    } catch (error) {
      console.error('Google Drive receipt import failed:', error);
      onError(error?.message || 'Could not import files from Google Drive.');
      setIsUploading(false);
      setUploadProgress(0);
      setProcessingStatus('');
    } finally {
      setIsImportingFromDrive(false);
    }
  };

  useEffect(() => {
    handleDriveImportRef.current = handleDriveImport;
  });

  useEffect(() => {
    if (initialDriveImportMode !== 'picker' || initialDriveImportStartedRef.current) return;

    initialDriveImportStartedRef.current = true;
    const timer = setTimeout(() => {
      handleDriveImportRef.current?.();
    }, 150);

    return () => clearTimeout(timer);
  }, [initialDriveImportMode]);

  const cropReceiptImageFromBounds = async (file, bounds) => {
    if (!file || !bounds || !file.type || !file.type.startsWith('image/')) {
      return null;
    }

    const sourceUrl = URL.createObjectURL(file);

    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = sourceUrl;
      });

      const padding = Math.round(Math.min(image.naturalWidth, image.naturalHeight) * 0.025);
      const left = Math.max(0, Math.floor((Number(bounds.left) || 0) - padding));
      const top = Math.max(0, Math.floor((Number(bounds.top) || 0) - padding));
      const right = Math.min(image.naturalWidth, Math.ceil((Number(bounds.right) || image.naturalWidth) + padding));
      const bottom = Math.min(image.naturalHeight, Math.ceil((Number(bounds.bottom) || image.naturalHeight) + padding));
      const width = Math.max(1, right - left);
      const height = Math.max(1, bottom - top);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.drawImage(image, left, top, width, height, 0, 0, width, height);
      return canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.92);
    } catch (error) {
      console.warn('Could not crop detected receipt image:', error);
      return null;
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  };

  const attachCroppedReceiptImages = async (receipts, file) => {
    if (!Array.isArray(receipts) || receipts.length === 0) return receipts;
    if (!file?.type?.startsWith('image/')) return receipts;

    return Promise.all(receipts.map(async (receipt) => {
      const croppedImageUrl = await cropReceiptImageFromBounds(file, receipt.bounds);
      return croppedImageUrl
        ? { ...receipt, imageUrl: croppedImageUrl, sourceImageUrl: receipt.imageUrl }
        : receipt;
    }));
  };

  const loadImageFromFile = async (file) => {
    const sourceUrl = URL.createObjectURL(file);

    try {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ image: img, sourceUrl });
        img.onerror = reject;
        img.src = sourceUrl;
      });
    } catch (error) {
      URL.revokeObjectURL(sourceUrl);
      throw error;
    }
  };

  const cropImageFileToReceiptFile = async (file, bounds, index) => {
    const { image, sourceUrl } = await loadImageFromFile(file);

    try {
      const left = Math.max(0, Math.floor(bounds.left));
      const top = Math.max(0, Math.floor(bounds.top));
      const right = Math.min(image.naturalWidth, Math.ceil(bounds.right));
      const bottom = Math.min(image.naturalHeight, Math.ceil(bounds.bottom));
      const width = Math.max(1, right - left);
      const height = Math.max(1, bottom - top);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.drawImage(image, left, top, width, height, 0, 0, width, height);

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.94);
      });

      if (!blob) return null;

      const extension = file.type === 'image/png' ? 'png' : 'jpg';
      const sourceName = String(file.name || 'receipt').replace(/\.[^.]+$/, '');
      return new File([blob], `${sourceName}-receipt-${index + 1}.${extension}`, {
        type: blob.type || file.type || 'image/jpeg'
      });
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  };

  const detectReceiptImageSegments = async (file) => {
    if (!file?.type?.startsWith('image/')) return [];

    try {
      const { image, sourceUrl } = await loadImageFromFile(file);

      try {
        const sampleWidth = 900;
        const scale = sampleWidth / image.naturalWidth;
        const sampleHeight = Math.max(1, Math.round(image.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = sampleWidth;
        canvas.height = sampleHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return [];

        context.drawImage(image, 0, 0, sampleWidth, sampleHeight);
        const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
        const sampleStride = 3;
        const maskWidth = Math.ceil(sampleWidth / sampleStride);
        const maskHeight = Math.ceil(sampleHeight / sampleStride);
        const mask = new Uint8Array(maskWidth * maskHeight);

        const looksLikeReceiptPaper = (r, g, b) => {
          const maxChannel = Math.max(r, g, b);
          const minChannel = Math.min(r, g, b);
          const brightness = (r + g + b) / 3;
          const saturation = maxChannel - minChannel;
          const isWhiteOrCreamPaper =
            brightness > 132 &&
            b > 88 &&
            saturation < 96 &&
            r - b < 96;
          const isOrangeOrPinkReceipt =
            r > 135 &&
            g > 45 &&
            b < 135 &&
            r > g * 1.12 &&
            g > b * 0.75;
          const isLightTintedPaper =
            brightness > 152 &&
            saturation < 135 &&
            maxChannel > 165;

          return isWhiteOrCreamPaper || isOrangeOrPinkReceipt || isLightTintedPaper;
        };

        for (let gridY = 0; gridY < maskHeight; gridY += 1) {
          const y = Math.min(sampleHeight - 1, gridY * sampleStride);
          for (let gridX = 0; gridX < maskWidth; gridX += 1) {
            const x = Math.min(sampleWidth - 1, gridX * sampleStride);
            const offset = (y * sampleWidth + x) * 4;
            if (looksLikeReceiptPaper(data[offset], data[offset + 1], data[offset + 2])) {
              mask[gridY * maskWidth + gridX] = 1;
            }
          }
        }

        const visited = new Uint8Array(mask.length);
        const components = [];
        const minComponentPixels = Math.max(80, Math.round(mask.length * 0.008));

        for (let startIndex = 0; startIndex < mask.length; startIndex += 1) {
          if (!mask[startIndex] || visited[startIndex]) continue;

          const queue = [startIndex];
          visited[startIndex] = 1;
          let queueIndex = 0;
          let count = 0;
          let minX = maskWidth;
          let maxX = 0;
          let minY = maskHeight;
          let maxY = 0;

          while (queueIndex < queue.length) {
            const current = queue[queueIndex];
            queueIndex += 1;
            count += 1;

            const x = current % maskWidth;
            const y = Math.floor(current / maskWidth);
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);

            const neighbors = [
              x > 0 ? current - 1 : -1,
              x < maskWidth - 1 ? current + 1 : -1,
              y > 0 ? current - maskWidth : -1,
              y < maskHeight - 1 ? current + maskWidth : -1
            ];

            neighbors.forEach((neighbor) => {
              if (neighbor >= 0 && mask[neighbor] && !visited[neighbor]) {
                visited[neighbor] = 1;
                queue.push(neighbor);
              }
            });
          }

          if (count >= minComponentPixels) {
            components.push({
              left: minX * sampleStride,
              right: Math.min(sampleWidth, (maxX + 1) * sampleStride),
              top: minY * sampleStride,
              bottom: Math.min(sampleHeight, (maxY + 1) * sampleStride),
              count
            });
          }
        }

        const mergeNearbyBoxes = (boxes) => {
          const merged = [];
          const proximityX = sampleWidth * 0.015;
          const proximityY = sampleHeight * 0.015;

          boxes.forEach((box) => {
            const existing = merged.find((candidate) => {
              const overlapWidth = Math.max(0, Math.min(box.right, candidate.right) - Math.max(box.left, candidate.left));
              const overlapHeight = Math.max(0, Math.min(box.bottom, candidate.bottom) - Math.max(box.top, candidate.top));
              const boxWidth = Math.max(1, box.right - box.left);
              const boxHeight = Math.max(1, box.bottom - box.top);
              const candidateWidth = Math.max(1, candidate.right - candidate.left);
              const candidateHeight = Math.max(1, candidate.bottom - candidate.top);
              const overlapArea = overlapWidth * overlapHeight;
              const minArea = Math.min(boxWidth * boxHeight, candidateWidth * candidateHeight);
              const horizontalOverlapRatio = overlapWidth / Math.min(boxWidth, candidateWidth);
              const verticalOverlapRatio = overlapHeight / Math.min(boxHeight, candidateHeight);
              const horizontalGap = Math.max(0, Math.max(box.left, candidate.left) - Math.min(box.right, candidate.right));
              const verticalGap = Math.max(0, Math.max(box.top, candidate.top) - Math.min(box.bottom, candidate.bottom));
              const significantlyOverlaps = overlapArea / minArea > 0.35;
              const isSmallFragment = minArea < sampleWidth * sampleHeight * 0.012;
              const sameColumnFragment = horizontalOverlapRatio > 0.72 && verticalGap <= proximityY;
              const sameRowFragment = verticalOverlapRatio > 0.72 && horizontalGap <= proximityX;

              return significantlyOverlaps || (isSmallFragment && (sameColumnFragment || sameRowFragment));
            });

            if (existing) {
              existing.left = Math.min(existing.left, box.left);
              existing.right = Math.max(existing.right, box.right);
              existing.top = Math.min(existing.top, box.top);
              existing.bottom = Math.max(existing.bottom, box.bottom);
              existing.count += box.count;
            } else {
              merged.push({ ...box });
            }
          });

          return merged;
        };

        const paddingX = Math.round(sampleWidth * 0.018);
        const paddingY = Math.round(sampleHeight * 0.022);
        const bounds = mergeNearbyBoxes(components)
          .map((box) => ({
            left: Math.max(0, (box.left - paddingX) / scale),
            right: Math.min(image.naturalWidth, (box.right + paddingX) / scale),
            top: Math.max(0, (box.top - paddingY) / scale),
            bottom: Math.min(image.naturalHeight, (box.bottom + paddingY) / scale)
          }))
          .filter((box) => {
          const width = box.right - box.left;
          const height = box.bottom - box.top;
          const area = width * height;
          return width > image.naturalWidth * 0.08 &&
            height > image.naturalHeight * 0.08 &&
            area > image.naturalWidth * image.naturalHeight * 0.018;
        })
          .sort((a, b) => {
            const rowDelta = a.top - b.top;
            if (Math.abs(rowDelta) > image.naturalHeight * 0.12) return rowDelta;
            return a.left - b.left;
          })
          .slice(0, 8);

        if (bounds.length <= 1) return [];

        const cropFiles = await Promise.all(bounds.map((box, index) =>
          cropImageFileToReceiptFile(file, box, index)
        ));

        return cropFiles.filter(Boolean);
      } finally {
        URL.revokeObjectURL(sourceUrl);
      }
    } catch (error) {
      console.warn('Could not segment receipt image before OCR:', error);
      return [];
    }
  };

  const inferBulkReceiptCategory = (receipt = {}) => {
    const text = `${receipt.rawText || receipt.text || ''} ${receipt.extractedData?.vendor || ''}`.toLowerCase();
    if (/parking|car\s*park|parkrite|apcoa|q-?park/.test(text)) return 'travel_expenses';
    if (/fuel|diesel|petrol|circle\s*k|service\s*station/.test(text)) return 'vehicle_fuel';
    if (/cement|solvent|paint|hardware|builders?|woodies|b&q|screwfix|chadwicks|co-?op\s+superstores/.test(text)) return 'repairs';
    if (/cleaning|detergent|bleach|cloth|towel|bin\s*bag/.test(text)) return 'cleaning';
    if (/beauty|salon|gel|nail|wax|cosmetic|boots/.test(text)) return 'supplies';
    return normalizeCategoryId(receipt.extractedData?.category || 'supplies');
  };

  const isPaymentOnlyReceipt = (receipt = {}) => {
    const text = String(receipt.rawText || receipt.text || '').toLowerCase();
    const items = Array.isArray(receipt.extractedData?.items) ? receipt.extractedData.items : [];
    const hasVatEvidence = /vat\s*analysis|vat\s*rate|net\s*goods|invoice\s+no/i.test(text);
    const hasPaymentEvidence = /debit\s+card|credit\s+card|contactless|auth\s*id|clover|retain this copy/i.test(text);
    return hasPaymentEvidence && !hasVatEvidence && items.length <= 1;
  };

  const mergeRelatedReceiptCopies = (receipts = []) => {
    const groups = [];

    receipts.forEach((receipt) => {
      const amount = parseAmountNumber(receipt.extractedData?.amount);
      if (amount === null) {
        groups.push([receipt]);
        return;
      }

      const matchingGroup = groups.find((group) => {
        const groupAmount = parseAmountNumber(group[0]?.extractedData?.amount);
        return groupAmount !== null && Math.abs(groupAmount - amount) < 0.02;
      });

      if (matchingGroup) {
        matchingGroup.push(receipt);
      } else {
        groups.push([receipt]);
      }
    });

    return groups.flatMap((group, groupIndex) => {
      if (group.length === 1) {
        const onlyReceipt = group[0];
        return [{
          ...onlyReceipt,
          extractedData: {
            ...(onlyReceipt.extractedData || {}),
            category: inferBulkReceiptCategory(onlyReceipt)
          }
        }];
      }

      if (!group.some(isPaymentOnlyReceipt)) {
        return group.map((receipt) => ({
          ...receipt,
          extractedData: {
            ...(receipt.extractedData || {}),
            category: inferBulkReceiptCategory(receipt)
          }
        }));
      }

      const sortedGroup = [...group].sort((a, b) => {
        const aScore =
          (isPaymentOnlyReceipt(a) ? -10 : 0) +
          (Array.isArray(a.extractedData?.items) ? a.extractedData.items.length : 0) +
          (Array.isArray(a.extractedData?.vatComponents) && a.extractedData.vatComponents.length > 0 ? 4 : 0) +
          (/invoice\s+no|vat\s*analysis|net\s*goods/i.test(a.rawText || a.text || '') ? 6 : 0);
        const bScore =
          (isPaymentOnlyReceipt(b) ? -10 : 0) +
          (Array.isArray(b.extractedData?.items) ? b.extractedData.items.length : 0) +
          (Array.isArray(b.extractedData?.vatComponents) && b.extractedData.vatComponents.length > 0 ? 4 : 0) +
          (/invoice\s+no|vat\s*analysis|net\s*goods/i.test(b.rawText || b.text || '') ? 6 : 0);
        return bScore - aScore;
      });

      const primary = sortedGroup[0];
      const paymentCopy = sortedGroup.find(isPaymentOnlyReceipt);
      const mergedText = group.map((receipt) => receipt.rawText || receipt.text || '').filter(Boolean).join('\n\n--- related receipt copy ---\n\n');
      const mergedData = {
        ...(primary.extractedData || {}),
        amount: primary.extractedData?.amount ?? paymentCopy?.extractedData?.amount ?? 0,
        date: primary.extractedData?.date || paymentCopy?.extractedData?.date,
        vendor: primary.extractedData?.vendor || paymentCopy?.extractedData?.vendor || '',
        category: inferBulkReceiptCategory({ ...primary, rawText: mergedText }),
        relatedReceiptCopies: group.length
      };

      return [{
        ...primary,
        id: `${primary.id || `receipt-${Date.now()}`}-merged-${groupIndex + 1}`,
        rawText: mergedText,
        text: mergedText,
        extractedData: mergedData,
        relatedReceipts: group
      }];
    });
  };

  const extractAmountFromReceiptText = (text = '', labelPattern = 'Total\\s+EUR') => {
    const regex = new RegExp(`${labelPattern}[\\s\\S]{0,50}?([€£$]?\\s*\\d+[.,]\\d{2})`, 'i');
    const match = String(text || '').match(regex);
    return match ? parseAmountNumber(match[1]) : null;
  };

  const deriveReceiptsFromCombinedOcrText = (result = {}) => {
    const rawText = String(result.rawText || result.text || result.fullText || '');
    if (!rawText || !/invoice\s+no\.?/i.test(rawText)) return [];

    const invoiceStarts = [...rawText.matchAll(/invoice\s+no\.?\s*[:#]?\s*[A-Z0-9-]+/gi)];
    if (invoiceStarts.length <= 1) return [];

    const fallbackCurrency = result.extractedData?.currency || defaultCurrencySymbol;
    const receipts = invoiceStarts.map((match, index) => {
      const start = match.index || 0;
      const end = index + 1 < invoiceStarts.length ? invoiceStarts[index + 1].index : rawText.length;
      const block = rawText.slice(start, end).trim();
      const amount =
        extractAmountFromReceiptText(block, 'Total\\s+EUR') ??
        extractAmountFromReceiptText(block, 'Total\\s+Amount\\s+Received') ??
        extractAmountFromReceiptText(block, '\\bTotal\\b');

      if (amount === null) return null;

      const vatAmount =
        extractAmountFromReceiptText(block, '\\bVAT\\b') ??
        (() => {
          const summaryMatch = block.match(/Net\s+Good[s.]?\s+VAT[\s\S]{0,90}?\d+[.,]\d{2}\s+(\d+[.,]\d{2})/i);
          return summaryMatch ? parseAmountNumber(summaryMatch[1]) : null;
        })();
      const vatRate = vatAmount !== null && vatAmount > 0 ? 0.23 : null;
      const vendor = /co-?op\s+superstores/i.test(block)
        ? 'Co-Op Superstores'
        : (result.extractedData?.vendor && result.extractedData.vendor !== '0 file(s) copied.'
            ? result.extractedData.vendor
            : 'Unknown Vendor');
      const category = inferBulkReceiptCategory({
        rawText: block,
        extractedData: { vendor }
      });
      const date = normalizeReceiptDateForInput(result.extractedData?.date);
      const itemMatch = block.match(/\b\d{6,}\s+([^\n]+?)(?:\s+\d+[.,]\d{2}|\n)/i);
      const itemDescription = itemMatch
        ? itemMatch[1].replace(/\s+/g, ' ').trim()
        : `${vendor} purchase`;

      return {
        id: `${result.id || `receipt-${Date.now()}`}-invoice-${index + 1}`,
        parentReceiptId: result.id || null,
        status: result.status || result.scanStatus || 'processed',
        imageUrl: result.imageUrl,
        rawText: block,
        text: block,
        extractedData: {
          date,
          amount,
          vendor,
          category,
          notes: 'Split from multi-receipt image',
          items: [{
            description: itemDescription,
            quantity: 1,
            unitPrice: amount,
            amount,
            category,
            vatRate,
            vatAmount
          }],
          vatAmount,
          vatRate,
          vatComponents: vatAmount !== null && vatRate !== null
            ? [{
                rate: vatRate,
                amount: vatAmount,
                description: `${(vatRate * 100).toFixed(1)}% VAT`
              }]
            : [],
          currency: fallbackCurrency,
          countryId: selectedCountry.id,
          relatedReceiptCopies: /debit\s+card|contactless|clover|retain this copy/i.test(block) ? 2 : 1
        },
        bulkIndex: index,
        bulkCount: invoiceStarts.length
      };
    }).filter(Boolean);

    const seen = new Set();
    const invoiceDerivedReceipts = receipts.filter((receipt) => {
      const key = `${receipt.extractedData.date}:${receipt.extractedData.amount}:${receipt.extractedData.vendor}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (invoiceDerivedReceipts.length > 1) {
      return invoiceDerivedReceipts;
    }

    const totalMatches = [...rawText.matchAll(/Total\s+EUR\s*\n?\s*([€£$]?\s*\d+[.,]\d{2})/gi)]
      .map((match) => parseAmountNumber(match[1]))
      .filter((amount) => amount !== null);
    const distinctTotals = [...new Set(totalMatches.map((amount) => amount.toFixed(2)))]
      .map((amount) => parseFloat(amount))
      .filter((amount) => amount > 0)
      .sort((a, b) => b - a);

    if (distinctTotals.length <= 1) {
      return invoiceDerivedReceipts;
    }

    const vendor = /co-?op\s+superstores/i.test(rawText)
      ? 'Co-Op Superstores'
      : (result.extractedData?.vendor && result.extractedData.vendor !== '0 file(s) copied.'
          ? result.extractedData.vendor
          : 'Unknown Vendor');
    const category = inferBulkReceiptCategory({
      rawText,
      extractedData: { vendor }
    });
    const date = normalizeReceiptDateForInput(result.extractedData?.date);

    return distinctTotals.map((amount, index) => {
      const calculatedVat = parseFloat((amount - (amount / 1.23)).toFixed(2));
      const amountPattern = String(amount.toFixed(2)).replace('.', '[.,]');
      const itemLineMatch = rawText.match(new RegExp(`([^\\n]{8,90})\\s+${amountPattern}(?=\\n|\\s|$)`, 'i'));
      const itemDescription = itemLineMatch
        ? itemLineMatch[1].replace(/\s+/g, ' ').trim()
        : `${vendor} purchase`;

      return {
        id: `${result.id || `receipt-${Date.now()}`}-total-${index + 1}`,
        parentReceiptId: result.id || null,
        status: result.status || result.scanStatus || 'processed',
        imageUrl: result.imageUrl,
        rawText,
        text: rawText,
        extractedData: {
          date,
          amount,
          vendor,
          category,
          notes: 'Split from multi-receipt image',
          items: [{
            description: itemDescription,
            quantity: 1,
            unitPrice: amount,
            amount,
            category,
            vatRate: 0.23,
            vatAmount: calculatedVat
          }],
          vatAmount: calculatedVat,
          vatRate: 0.23,
          vatComponents: [{
            rate: 0.23,
            amount: calculatedVat,
            description: '23.0% VAT'
          }],
          currency: fallbackCurrency,
          countryId: selectedCountry.id,
          relatedReceiptCopies: /debit\s+card|contactless|clover|retain this copy/i.test(rawText) ? 2 : 1
        },
        bulkIndex: index,
        bulkCount: distinctTotals.length
      };
    });
  };

  const uploadAndScanReceiptBatch = async (files) => {
    console.log('uploadAndScanReceiptBatch called');
    setIsUploading(true);
    setUploadProgress(0);
    setProcessingStatus(`Preparing ${files.length} receipts for processing...`);

    try {
      const scannedReceipts = [];

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadProgress(Math.round((index / files.length) * 80) + 10);
        setProcessingStatus(`Analyzing receipt ${index + 1} of ${files.length}...`);

        const visionResult = await receiptScannerService.scanReceipt(file, {
          countryId: selectedCountry.id,
          currency: defaultCurrencySymbol,
          workspaceId: activeWorkspace?.id || null,
          workspaceOwnerId: activeWorkspace?.ownerId || null,
          source: file.driveMetadata ? 'google_drive_picker' : 'manual_upload',
          sourceDocumentId: file.driveMetadata?.id || null,
          sourceDocumentName: file.driveMetadata?.name || file.name || null,
          sourceDocumentModifiedTime: file.driveMetadata?.modifiedTime || null,
          sourceDocumentChecksum: file.driveMetadata?.md5Checksum || null,
          sourceFolderId: file.driveMetadata?.folderId || null,
          sourceFolderName: file.driveMetadata?.folderName || null
        });

        if (Array.isArray(visionResult?.bulkReceipts) && visionResult.bulkReceipts.length > 1) {
          const croppedBulkReceipts = await attachCroppedReceiptImages(visionResult.bulkReceipts, file);
          scannedReceipts.push(...croppedBulkReceipts);
        } else if (visionResult) {
          scannedReceipts.push({
            ...visionResult,
            bulkIndex: scannedReceipts.length,
            bulkCount: files.length
          });
        }
      }

      if (scannedReceipts.length === 0) {
        throw new Error('No OCR data returned. Please enter the details manually.');
      }

      const mergedReceipts = mergeRelatedReceiptCopies(scannedReceipts);
      const queue = mergedReceipts.map((receipt, index) => {
        let extractedData = receipt.extractedData || {};
        extractedData = sanitizeReceiptData({
          ...extractedData,
          category: normalizeCategoryId(extractedData.category || inferBulkReceiptCategory(receipt)),
          amount: extractedData.amount
        });
        extractedData = applyReceiptTextCorrections(
          extractedData,
          receipt.rawText || receipt.text || receipt.fullText || ''
        );

        return {
          ...receipt,
          extractedData,
          bulkIndex: index,
          bulkCount: mergedReceipts.length
        };
      });

      setBulkReceiptQueue(queue.length > 1 ? queue : []);
      setBulkReceiptIndex(0);
      setScannedReceipt(queue[0]);
      processExtractedData(queue[0].extractedData, queue[0].rawText || queue[0].text || queue[0].fullText || '');

      setUploadProgress(100);
      setProcessingStatus(queue.length > 1
        ? `${queue.length} receipts detected. Review each receipt before saving.`
        : 'Receipt processed successfully!');

      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setProcessingStatus('');
      }, 1500);
    } catch (error) {
      console.error('Bulk receipt scanning failed:', error);
      onError(error?.message || 'Failed to scan receipts. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
      setProcessingStatus('');
    }
  };
  
  /**
   * Upload and scan a receipt
   */
  const uploadAndScanReceipt = async (file) => {
    console.log('uploadAndScanReceipt called');
    setIsUploading(true);
    setUploadProgress(0);
    setProcessingStatus('Preparing receipt for processing...');

    try {
      // Show initial progress
      setUploadProgress(10);
      setProcessingStatus('Connecting to OCR service...');
      
      // Simulate some initial progress
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadProgress(20);
      setProcessingStatus('Processing receipt image...');

      if (file?.type?.startsWith('image/')) {
        setProcessingStatus('Checking for multiple receipts in the image...');
        const segmentedReceiptFiles = await detectReceiptImageSegments(file);
        if (segmentedReceiptFiles.length > 1) {
          setProcessingStatus(`Found ${segmentedReceiptFiles.length} receipt slips. Analyzing each one...`);
          await uploadAndScanReceiptBatch(segmentedReceiptFiles);
          return;
        }
      }

      // Process receipt with Receipt Scanner service
      let result;
      const buildManualResult = (errorMessage) => ({
        id: `receipt-${Date.now()}`,
        imageUrl: URL.createObjectURL(file),
        extractedData: {
          date: new Date().toISOString().split('T')[0],
          amount: 0,
          vendor: '',
          category: 'supplies',
          items: [],
          vatAmount: null,
          vatRate: null,
          currency: defaultCurrencySymbol,
          countryId: selectedCountry.id
        },
        rawText: '',
        scanStatus: 'manual',
        error: errorMessage || null
      });
      try {
        console.log('Using Receipt Scanner service for processing');
        setUploadProgress(40);
        setProcessingStatus('Analyzing receipt with OCR...');
        
        const visionResult = await receiptScannerService.scanReceipt(file, {
          countryId: selectedCountry.id,
          currency: defaultCurrencySymbol,
          workspaceId: activeWorkspace?.id || null,
          workspaceOwnerId: activeWorkspace?.ownerId || null,
          source: file.driveMetadata ? 'google_drive_picker' : 'manual_upload',
          sourceDocumentId: file.driveMetadata?.id || null,
          sourceDocumentName: file.driveMetadata?.name || file.name || null,
          sourceDocumentModifiedTime: file.driveMetadata?.modifiedTime || null,
          sourceDocumentChecksum: file.driveMetadata?.md5Checksum || null,
          sourceFolderId: file.driveMetadata?.folderId || null,
          sourceFolderName: file.driveMetadata?.folderName || null
        });
        
        setUploadProgress(80);
        setProcessingStatus('Extracting transaction data...');

        const buildManualResult = (errorMessage) => ({
          id: `receipt-${Date.now()}`,
          imageUrl: URL.createObjectURL(file),
          extractedData: {
            date: new Date().toISOString().split('T')[0],
            amount: 0,
            vendor: '',
            category: 'supplies',
            items: [],
            vatAmount: null,
            vatRate: null,
            currency: defaultCurrencySymbol,
            countryId: selectedCountry.id
          },
          rawText: '',
          scanStatus: 'manual',
          error: errorMessage || null
        });

        if (visionResult) {
          // Extract data from scanner result
          let extractedData;
          
          // Check if we have extractedData in the response
          if (visionResult.extractedData) {
            console.log('Using extracted data from API response:', visionResult.extractedData);
            extractedData = visionResult.extractedData;
          } else {
            console.log('No extracted data in API response, using defaults');
            extractedData = {
              date: new Date().toISOString().split('T')[0],
              amount: 0,
              vendor: 'Unknown Vendor',
              category: 'supplies',
              items: [],
              vatAmount: null,
              vatRate: null,
              currency: defaultCurrencySymbol,
              countryId: selectedCountry.id
            };
          }
          
          // Debug all important fields
          console.log('Data from API:', {
            amount: extractedData.amount, 
            vendor: extractedData.vendor,
            category: extractedData.category,
            date: extractedData.date
          });
          
          // Force the category to vehicle fuel for Circle K receipts
          if (extractedData.vendor && extractedData.vendor.toLowerCase().includes('circle k')) {
            console.log('Detected Circle K receipt, setting category to vehicle fuel');
            extractedData.category = 'vehicle_fuel';
          }
          
          // Force the category to be 'repairs' for hardware/DIY store receipts
          if (extractedData.vendor && /woodies|hardware|diy|b\s*&\s*q|home\s*depot|lowe's|homebase|chadwicks|screwfix/i.test(extractedData.vendor.toLowerCase())) {
            console.log('Detected hardware/DIY store receipt, setting category to repairs');
            extractedData.category = 'repairs';
          }
          
          result = {
            id: visionResult.id,
            imageUrl: visionResult.imageUrl,
            extractedData: extractedData,
            rawText: visionResult.rawText || visionResult.text || visionResult.fullText || '',
            scanStatus: visionResult.status || 'processed',
            bulkReceipts: Array.isArray(visionResult.bulkReceipts) ? visionResult.bulkReceipts : []
          };
          if (result.bulkReceipts.length <= 1) {
            const textDerivedReceipts = deriveReceiptsFromCombinedOcrText(result);
            if (textDerivedReceipts.length > 1) {
              console.log(`Derived ${textDerivedReceipts.length} receipts from combined OCR text`);
              result.bulkReceipts = textDerivedReceipts;
            }
          }
          console.log('Successfully processed receipt with Receipt Scanner service');
          console.log('Extracted data:', extractedData);
        } else {
          // If scanner returns no result, default to manual entry (avoid fake data in production)
          result = buildManualResult('No OCR data returned. Please enter the details manually.');
        }
      } catch (error) {
        console.warn('Receipt scanning failed:', error);
        result = buildManualResult(error?.message || 'Receipt scanning failed. Please enter the details manually.');
      }

      // Set progress to 100%
      setUploadProgress(100);

      // Normalize category before persisting
      if (result && result.extractedData) {
        const rawTextForScanResult = result.rawText || result.text || result.fullText || '';
        const dateFromReceiptText = extractReceiptDateForInputFromText(rawTextForScanResult);
        let sanitizedExtractedData = sanitizeReceiptData({
          ...result.extractedData,
          date: dateFromReceiptText || result.extractedData.date,
          category: normalizeCategoryId(result.extractedData.category),
          amount: result.extractedData.amount
        });
        sanitizedExtractedData = applyReceiptTextCorrections(
          sanitizedExtractedData,
          result.rawText || result.text || result.fullText || ''
        );

        result = {
          ...result,
          extractedData: sanitizedExtractedData
        };
      }

      // Update state with scanned receipt data
      const detectedBulkCount = Array.isArray(result?.bulkReceipts) ? result.bulkReceipts.length : 0;
      if (detectedBulkCount > 1) {
        const croppedBulkReceipts = await attachCroppedReceiptImages(result.bulkReceipts, file);
        const bulkReceipts = mergeRelatedReceiptCopies(croppedBulkReceipts).map((receipt, index, receipts) => ({
          ...receipt,
          bulkIndex: index,
          bulkCount: receipts.length
        }));
        setBulkReceiptQueue(bulkReceipts);
        setBulkReceiptIndex(0);
        result = bulkReceipts[0];
      } else {
        setBulkReceiptQueue([]);
        setBulkReceiptIndex(0);
      }

      setScannedReceipt(result);

      // Add this right after setScannedReceipt(result);
      if (result && result.extractedData) {
        // Force the field values directly and lock them to prevent race conditions
        const fieldData = {
          date: result.extractedData.date || new Date().toISOString().split('T')[0],
          amount: result.extractedData.amount || 0,
          vendor: result.extractedData.vendor || 'Unknown Vendor',
          category: result.extractedData.category || 'supplies'
        };
        
        // Use setTimeout to ensure React has updated the DOM
        setTimeout(() => {
          lockFormValues(fieldData);
        }, 600);
      }

      // Pre-fill metadata with extracted information
      processExtractedData(result.extractedData, result.rawText || result.text || result.fullText || '');

      // Show completion (or manual entry prompt)
      setUploadProgress(100);
      setProcessingStatus(result?.scanStatus === 'manual'
        ? 'Could not auto-scan this receipt. Please check and fill in the details manually.'
        : detectedBulkCount > 1
          ? `${detectedBulkCount} receipts detected. Review each receipt before saving.`
          : 'Receipt processed successfully!');

      // Reset upload state after a delay
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setProcessingStatus('');
      }, 1500);

    } catch (error) {
      console.error('Receipt scanning failed:', error);
      onError('Failed to scan receipt. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
      setProcessingStatus('');
    }
  };
  
  /**
   * Generate mock result for testing or when scanning fails
   */
  const getMockResult = (file) => {
    console.warn('Generating mock data');
    const imageUrl = URL.createObjectURL(file);
    const receiptId = `receipt-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // Generate mock data based on the filename
    const filename = file.name.toLowerCase();
    let mockData = {
      vendor: 'Unknown Vendor',
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      category: 'supplies'
    };

    // Try to extract date from filename (common formats like YYYY-MM-DD or DD-MM-YYYY)
    const dateMatch = filename.match(/(\d{4}[-_]\d{2}[-_]\d{2})|(\d{2}[-_]\d{2}[-_]\d{4})/);
    if (dateMatch) {
      const dateStr = dateMatch[0].replace(/_/g, '-');
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // YYYY-MM-DD format
        mockData.date = dateStr;
      } else if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        // DD-MM-YYYY format
        const parts = dateStr.split('-');
        mockData.date = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    // Try to extract amount from filename (e.g., 24.99)
    const amountMatch = filename.match(/(\d+\.\d{2})|(\d+,\d{2})/);
    if (amountMatch) {
      mockData.amount = parseFloat(amountMatch[0].replace(',', '.'));
    }

    return {
      id: receiptId,
      status: 'processed',
      imageUrl: imageUrl,
      extractedData: mockData,
      confidence: 0.5,
      processedDate: new Date().toISOString(),
      text: 'No text extracted'
    };
  };
  
  /**
   * Process extracted data from receipt scanner
   */
  const processExtractedData = (extractedData, rawText = '') => {
    if (!extractedData) return;
    
    // Create a deep copy to avoid reference issues
    let processedData = sanitizeReceiptData(JSON.parse(JSON.stringify(extractedData)));
    const countryInput = processedData.countryId || receiptMetadata.countryId || selectedCountry.id;
    processedData = normalizeVatDataForCountry(processedData, {
      country: countryInput,
      text: rawText || processedData.rawText || processedData.text || processedData.fullText || ''
    });
    const rawTextForCorrections = rawText || processedData.rawText || processedData.text || processedData.fullText || '';
    processedData = applyReceiptTextCorrections(processedData, rawTextForCorrections);
    processedData.currency = normalizeCurrencySymbol(processedData.currency, countryInput) || defaultCurrencySymbol;
    
    console.log('ORIGINAL RECEIPT DATA INCOMING:', processedData);
    
    // IMPORTANT: Preserve original values - don't override with defaults
    // Don't set defaults unless the values are actually missing
    const textDate = extractReceiptDateForInputFromText(rawTextForCorrections);
    processedData.date = normalizeReceiptDateForInput(textDate || processedData.date);
    
    // Preserve original vendor if it exists
    if (!processedData.vendor || processedData.vendor.trim() === '') {
      processedData.vendor = 'Unknown Vendor';
    }
    
    const validCategoryIds = new Set(categoryOptions.map((category) => normalizeCategoryId(category?.id || '')));
    const processedCategory = normalizeCategoryId(processedData.category || '');
    const fallbackCategory = isIncomeUpload
      ? (categoryOptions[0]?.id || 'nail_services')
      : (expenseCategoryOptions[0]?.id || 'supplies');
    processedData.category = validCategoryIds.has(processedCategory)
      ? processedCategory
      : normalizeCategoryId(fallbackCategory);
    
    // CRITICAL: Preserve the original amount
    const amount = normalizeAmount(
      processedData.amount !== undefined && processedData.amount !== null ? processedData.amount : ''
    );
    
    console.log('PROCESSED RECEIPT DATA:', {
      date: processedData.date,
      amount: amount,
      vendor: processedData.vendor,
      category: processedData.category
    });
    
    // Set the entire state at once instead of using the previous state
    setReceiptMetadata({
      date: processedData.date,
      amount: amount,
      vendor: processedData.vendor,
      category: processedData.category,
      countryId: countryInput,
      suggestedCategoryName: processedData.suggestedCategoryName || '',
      vatAmount: processedData.vatAmount ?? '',
      vatRate: processedData.vatRate ?? '',
      vatComponents: Array.isArray(processedData.vatComponents) ? processedData.vatComponents : [],
      currency: processedData.currency || defaultCurrencySymbol,
      notes: processedData.notes || '',
      items: Array.isArray(processedData.items) ? processedData.items : [],
      clientId: '',
      clientName: '',
      paymentStatus: 'paid',
      amountPaid: amount,
      paymentDate: processedData.date,
      paymentMethod: paymentMethods[0]?.id || 'card',
      allocateToClient: false,
      jobReference: '',
      billableToClient: false
    });
    
    // Directly update DOM elements with a longer timeout
    setTimeout(() => {
      // Explicitly set the field values using their IDs
      const fieldMap = {
        'date': processedData.date,
        'amount': amount,
        'vendor': processedData.vendor,
        'category': processedData.category
      };
      
      console.log('FORCING UI UPDATE WITH:', fieldMap);
      
      // Update each field and trigger proper events
      Object.entries(fieldMap).forEach(([fieldId, value]) => {
        const field = document.getElementById(fieldId);
        if (field) {
          field.value = value;
          
          // Different event for select vs input elements
          const eventType = field.tagName.toLowerCase() === 'select' ? 'change' : 'input';
          const event = new Event(eventType, { bubbles: true });
          field.dispatchEvent(event);
          
          console.log(`Forced ${fieldId} to value: ${value}`);
        }
      });
    }, 500); // Longer timeout to ensure React has finished rendering
    
    // Force a UI update with a slight delay to ensure React has processed the state change
    // Use the forceFieldValues method to update the DOM directly
    forceFieldValues({
      date: processedData.date,
      amount: amount,
      vendor: processedData.vendor,
      category: processedData.category
    });
    
    // Trigger a re-render
    setRenderTrigger(Date.now());
  };
  
  // handleMetadataChange function is already defined above
  
  /**
   * Force field values directly in the DOM
   */
  const forceFieldValues = (data) => {
    // Force field values directly in the DOM
    setTimeout(() => {
      if (data.date) {
        const dateField = document.getElementById('date');
        if (dateField) {
          dateField.value = data.date;
          const event = new Event('input', { bubbles: true });
          dateField.dispatchEvent(event);
        }
      }
      
      if (data.vendor) {
        const vendorField = document.getElementById('vendor');
        if (vendorField) {
          vendorField.value = data.vendor;
          const event = new Event('input', { bubbles: true });
          vendorField.dispatchEvent(event);
        }
      }
      
      if (data.category) {
        const categoryField = document.getElementById('category');
        if (categoryField) {
          categoryField.value = data.category;
          const event = new Event('change', { bubbles: true });
          categoryField.dispatchEvent(event);
        }
      }
      
      console.log('Directly forced field values:', data);
    }, 300);
  };
  
  /**
   * Lock form values to prevent race conditions
   */
  const unlockForm = () => {
    console.log('UNLOCKING FORM');
    const dateField = document.getElementById('date');
    const vendorField = document.getElementById('vendor');
    const categoryField = document.getElementById('category');
    const fields = [dateField, vendorField, categoryField].filter(Boolean);

    fields.forEach((field) => {
      if (field._lockChangeListener) {
        field.removeEventListener('change', field._lockChangeListener);
        field.removeEventListener('input', field._lockChangeListener);
        delete field._lockChangeListener;
      }
      if (field._categoryChangeListener) {
        field.removeEventListener('change', field._categoryChangeListener);
        delete field._categoryChangeListener;
      }
      if (field._preventRecursion) {
        delete field._preventRecursion;
      }
    });

    setFormLocked(false);
  };
  
  const lockFormValues = (data) => {
    // Store the original values
    const originalValues = {
      date: data.date,
      vendor: data.vendor, 
      category: data.category
    };
    
    console.log('LOCKING FORM VALUES:', originalValues);
    
    // Set form as locked to prevent handleMetadataChange from modifying values
    setFormLocked(true);
    
    // Force values initially
    forceFieldValues(originalValues);
    
    // Also update the React state directly
    setReceiptMetadata(prev => ({
      ...prev,
      date: data.date,
      vendor: data.vendor,
      category: data.category
    }));
    
    // Use multiple timeouts to continually reset the fields
    // This ensures no other code can overwrite them
    const intervals = [100, 250];
    
    intervals.forEach(delay => {
      setTimeout(() => {
        forceFieldValues(originalValues);
      }, delay);
    });
    
    // Keep values stable until async OCR writes settle, then unlock to allow edits.
    const unlockDelay = Math.max(...intervals) + 150;
    setTimeout(() => {
      unlockForm();
    }, unlockDelay);
      
  };
  
  const getCurrentLineItems = () => (
    Array.isArray(scannedReceipt?.extractedData?.items) ? scannedReceipt.extractedData.items : []
  );

  const calculateLineItemsTotal = (lineItems = []) => {
    let hasAnyAmount = false;
    const total = lineItems.reduce((sum, item) => {
      let itemAmount = parseAmountNumber(item?.amount);
      if (itemAmount === null) {
        const itemQuantity = parseAmountNumber(item?.quantity);
        const itemUnitPrice = parseAmountNumber(item?.unitPrice);
        if (itemQuantity !== null && itemUnitPrice !== null) {
          itemAmount = itemQuantity * itemUnitPrice;
        } else if (itemUnitPrice !== null) {
          itemAmount = itemUnitPrice;
        }
      }

      if (itemAmount === null) {
        return sum;
      }

      hasAnyAmount = true;
      return sum + itemAmount;
    }, 0);

    return hasAnyAmount ? roundToTwoDecimals(total) : null;
  };

  const normalizeEditableItem = (item = {}, index = 0) => {
    const itemQuantity = parseAmountNumber(item.quantity);
    const itemAmount = parseAmountNumber(item.amount);
    const itemUnitPrice = parseAmountNumber(item.unitPrice);

    let resolvedAmount = itemAmount;
    let resolvedUnitPrice = itemUnitPrice;

    if (resolvedAmount === null && resolvedUnitPrice !== null && itemQuantity !== null && itemQuantity !== 0) {
      resolvedAmount = roundToTwoDecimals(resolvedUnitPrice * itemQuantity);
    }

    if (resolvedUnitPrice === null && resolvedAmount !== null && itemQuantity !== null && itemQuantity !== 0) {
      resolvedUnitPrice = roundToTwoDecimals(resolvedAmount / itemQuantity);
    }

    return {
      ...item,
      description: item.description ?? item.name ?? item.item ?? `Item ${index + 1}`,
      quantity: itemQuantity === null ? (item.quantity ?? 1) : itemQuantity,
      unitPrice: resolvedUnitPrice,
      amount: resolvedAmount,
      category: normalizeCategoryId(item.category || receiptMetadata.category || 'supplies')
    };
  };

  const applyUpdatedItems = (updatedItems, options = {}) => {
    if (!scannedReceipt || !scannedReceipt.extractedData) {
      return;
    }

    const { syncTotal = true } = options;
    const normalizedItems = (Array.isArray(updatedItems) ? updatedItems : []).map((item, index) =>
      normalizeEditableItem(item, index)
    );

    setScannedReceipt({
      ...scannedReceipt,
      extractedData: {
        ...scannedReceipt.extractedData,
        items: normalizedItems
      }
    });

    setReceiptMetadata((prev) => {
      const next = {
        ...prev,
        items: normalizedItems
      };

      if (syncTotal) {
        const calculatedTotal = calculateLineItemsTotal(normalizedItems);
        if (calculatedTotal !== null) {
          next.amount = calculatedTotal.toFixed(2);
        }
      }

      return next;
    });
  };

  /**
   * Handle category change for a specific line item
   */
  const handleItemCategoryChange = (itemIndex, category) => {
    const updatedItems = [...getCurrentLineItems()];
    if (!updatedItems[itemIndex]) return;

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      category: normalizeCategoryId(category)
    };

    applyUpdatedItems(updatedItems, { syncTotal: false });
  };

  // Toggle whether a specific line item should be saved to the Cost Calculator
  const handleItemSaveToggle = (itemIndex, checked) => {
    const updatedItems = [...getCurrentLineItems()];
    if (!updatedItems[itemIndex]) return;

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      saveToCost: checked
    };

    applyUpdatedItems(updatedItems, { syncTotal: false });
  };

  const handleItemDescriptionChange = (itemIndex, value) => {
    const updatedItems = [...getCurrentLineItems()];
    if (!updatedItems[itemIndex]) return;

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      description: value
    };

    applyUpdatedItems(updatedItems, { syncTotal: false });
  };

  const handleItemQuantityChange = (itemIndex, value) => {
    const updatedItems = [...getCurrentLineItems()];
    const currentItem = updatedItems[itemIndex];
    if (!currentItem) return;

    const quantityValue = parseAmountNumber(value);
    const hasValidQty = quantityValue !== null && quantityValue > 0;
    const currentAmount = parseAmountNumber(currentItem.amount);
    const currentUnitPrice = parseAmountNumber(currentItem.unitPrice);

    const nextItem = {
      ...currentItem,
      quantity: value === '' ? '' : quantityValue
    };

    // Preserve total paid if amount exists; update unit price from amount/qty.
    if (hasValidQty && currentAmount !== null) {
      nextItem.unitPrice = roundToTwoDecimals(currentAmount / quantityValue);
    } else if (hasValidQty && currentUnitPrice !== null) {
      // If no total exists, derive total from unit price.
      nextItem.amount = roundToTwoDecimals(currentUnitPrice * quantityValue);
    }

    updatedItems[itemIndex] = nextItem;
    applyUpdatedItems(updatedItems);
  };

  const handleItemUnitPriceChange = (itemIndex, value) => {
    const updatedItems = [...getCurrentLineItems()];
    const currentItem = updatedItems[itemIndex];
    if (!currentItem) return;

    const nextUnitPrice = parseAmountNumber(value);
    const quantityValue = parseAmountNumber(currentItem.quantity);
    const hasValidQty = quantityValue !== null && quantityValue > 0;

    updatedItems[itemIndex] = {
      ...currentItem,
      unitPrice: value === '' ? '' : nextUnitPrice,
      amount:
        nextUnitPrice === null
          ? ''
          : hasValidQty
            ? roundToTwoDecimals(nextUnitPrice * quantityValue)
            : roundToTwoDecimals(nextUnitPrice)
    };

    applyUpdatedItems(updatedItems);
  };

  const handleItemAmountChange = (itemIndex, value) => {
    const updatedItems = [...getCurrentLineItems()];
    const currentItem = updatedItems[itemIndex];
    if (!currentItem) return;

    const nextAmount = parseAmountNumber(value);
    const quantityValue = parseAmountNumber(currentItem.quantity);
    const hasValidQty = quantityValue !== null && quantityValue > 0;

    updatedItems[itemIndex] = {
      ...currentItem,
      amount: value === '' ? '' : nextAmount,
      unitPrice:
        nextAmount === null
          ? currentItem.unitPrice
          : hasValidQty
            ? roundToTwoDecimals(nextAmount / quantityValue)
            : currentItem.unitPrice
    };

    applyUpdatedItems(updatedItems);
  };

  const handleAddLineItem = () => {
    const defaultCategory = normalizeCategoryId(receiptMetadata.category || 'supplies');
    const updatedItems = [
      ...getCurrentLineItems(),
      {
        description: '',
        quantity: 1,
        unitPrice: '',
        amount: '',
        category: defaultCategory,
        saveToCost: true
      }
    ];

    applyUpdatedItems(updatedItems, { syncTotal: false });
  };

  const handleRemoveLineItem = (itemIndex) => {
    const updatedItems = getCurrentLineItems().filter((_, index) => index !== itemIndex);
    applyUpdatedItems(updatedItems);
  };

  const handleSplitLineItem = (itemIndex) => {
    const currentItems = getCurrentLineItems();
    const sourceItem = currentItems[itemIndex];
    if (!sourceItem) return;

    const sourceAmount = parseAmountNumber(sourceItem.amount);
    const sourceQuantity = parseAmountNumber(sourceItem.quantity);

    const firstItem = { ...sourceItem };
    const secondItem = {
      ...sourceItem,
      description: sourceItem.description ? `${sourceItem.description} (split)` : `Item ${itemIndex + 2}`
    };

    if (sourceAmount !== null) {
      const firstAmount = roundToTwoDecimals(sourceAmount / 2);
      const secondAmount = roundToTwoDecimals(sourceAmount - firstAmount);
      firstItem.amount = firstAmount;
      secondItem.amount = secondAmount;
    }

    if (sourceQuantity !== null && sourceQuantity > 0) {
      const firstQuantity = roundToTwoDecimals(sourceQuantity / 2);
      const secondQuantity = roundToTwoDecimals(sourceQuantity - firstQuantity);
      firstItem.quantity = firstQuantity;
      secondItem.quantity = secondQuantity;

      if (parseAmountNumber(firstItem.amount) !== null && firstQuantity > 0) {
        firstItem.unitPrice = roundToTwoDecimals(parseAmountNumber(firstItem.amount) / firstQuantity);
      }

      if (parseAmountNumber(secondItem.amount) !== null && secondQuantity > 0) {
        secondItem.unitPrice = roundToTwoDecimals(parseAmountNumber(secondItem.amount) / secondQuantity);
      }
    }

    const updatedItems = [...currentItems];
    updatedItems.splice(itemIndex, 1, firstItem, secondItem);
    applyUpdatedItems(updatedItems);
  };

  /**
   * Add a new VAT component
   */
  const addVatComponent = () => {
    setReceiptMetadata(prev => {
      const newComponent = {
        rate: defaultVatRateString,
        amount: "0",
        description: defaultVatRateLabel
      };
      
      return {
        ...prev,
        vatComponents: [...(prev.vatComponents || []), newComponent]
      };
    });
  };
  
  /**
   * Remove a VAT component
   */
  const removeVatComponent = (index) => {
    setReceiptMetadata(prev => {
      const updatedComponents = [...(prev.vatComponents || [])];
      updatedComponents.splice(index, 1);
      
      return {
        ...prev,
        vatComponents: updatedComponents
      };
    });
  };

  const loadBulkReceiptForReview = (nextIndex) => {
    const nextReceipt = bulkReceiptQueue[nextIndex];
    if (!nextReceipt) return false;

    setBulkReceiptIndex(nextIndex);
    setScannedReceipt(nextReceipt);
    processExtractedData(
      nextReceipt.extractedData,
      nextReceipt.rawText || nextReceipt.text || nextReceipt.fullText || ''
    );
    setActiveTab('details');
    unlockForm();

    return true;
  };

  const saveAllBulkReceipts = async () => {
    const queue = bulkReceiptQueue.length > 1 ? bulkReceiptQueue : scannedReceipt ? [scannedReceipt] : [];
    if (queue.length === 0) return;

    try {
      for (let index = 0; index < queue.length; index += 1) {
        const receipt = queue[index];
        const data = sanitizeReceiptData(receipt.extractedData || {});
        const transactionDate = normalizeReceiptDateForInput(data.date);
        const items = Array.isArray(data.items) ? data.items : [];
        let receiptRecordForTransaction = receipt;

        if (auth.currentUser) {
          try {
            const rawImageUrl = receipt?.imageUrl || receipt?.receiptImageUrl || '';
            const imageUrl =
              typeof rawImageUrl === 'string' && /^https?:\/\//i.test(rawImageUrl)
                ? rawImageUrl
                : '';
            const createdReceipt = await createReceipt({
              imageUrl,
              status: receipt?.status || 'processed',
              scanStatus: receipt?.scanStatus || receipt?.status || 'processed',
              extractedData: {
                ...data,
                date: transactionDate
              },
              rawText: receipt.rawText || receipt.text || '',
              sourceReceiptId: receipt.parentReceiptId || receipt.receiptId || null,
              splitReceiptIndex: index + 1,
              splitReceiptCount: queue.length
            }, { workspace: activeWorkspace });

            if (createdReceipt?.id) {
              receiptRecordForTransaction = {
                ...receipt,
                ...createdReceipt,
                id: createdReceipt.id,
                receiptId: createdReceipt.id,
                extractedData: {
                  ...data,
                  date: transactionDate
                }
              };
              notifyReceiptCommitted({ id: createdReceipt.id });
            }
          } catch (createError) {
            console.warn('Failed to create split receipt record before transaction save:', createError);
          }
        }

        await Promise.resolve(onReceiptProcessed({
          id: `${receiptRecordForTransaction.id || `bulk-receipt-${Date.now()}`}-transaction-${index + 1}`,
          date: transactionDate,
          amount: parseAmountNumber(data.amount) || 0,
          description: data.vendor || `Receipt ${index + 1}`,
          category: normalizeCategoryId(data.category || 'supplies'),
          vatAmount: parseAmountNumber(data.vatAmount),
          vatRate: parseVatRateNumber(data.vatRate),
          vatComponents: Array.isArray(data.vatComponents) ? data.vatComponents : [],
          currency: data.currency || defaultCurrencySymbol,
          notes: data.notes || '',
          items,
          categoryBreakdown: [],
          isSplitTransaction: items.length > 1,
          type: 'expenses',
          receiptId: receiptRecordForTransaction.id,
          receiptImageUrl: receiptRecordForTransaction.imageUrl || receipt.imageUrl,
          timestamp: new Date().getTime() + index,
          keepReceiptScannerOpen: index < queue.length - 1
        }));
      }

      setBulkReceiptQueue([]);
      setBulkReceiptIndex(0);
      resetForm({ preserveReceiptRecord: true });
    } catch (error) {
      console.error('Failed to save bulk receipts:', error);
      onError('Failed to save all receipts. Please review and save them one by one.');
    }
  };
  
  /**
   * Convert receipt to transaction
   */
  const convertToTransaction = async () => {
    try {
      console.log('Converting receipt to transaction:', scannedReceipt);
      console.log('Receipt metadata:', receiptMetadata);
      notifyReceiptCommitted(scannedReceipt);

      // Ensure we have a valid date in ISO format.
      const transactionDate = normalizeReceiptDateForInput(receiptMetadata.date);
      const invoiceTotalAmountRaw = parseAmountNumber(receiptMetadata.amount);
      const invoiceTotalAmount = invoiceTotalAmountRaw === null ? 0 : roundToTwoDecimals(invoiceTotalAmountRaw);
      const invoicePaymentStatus = isIncomeUpload
        ? (['paid', 'unpaid', 'part_paid'].includes(receiptMetadata.paymentStatus) ? receiptMetadata.paymentStatus : 'paid')
        : 'paid';
      const parsedAmountPaid = parseAmountNumber(receiptMetadata.amountPaid);
      const invoiceAmountPaid = isIncomeUpload
        ? invoicePaymentStatus === 'paid'
          ? invoiceTotalAmount
          : invoicePaymentStatus === 'unpaid'
            ? 0
            : roundToTwoDecimals(parsedAmountPaid || 0)
        : invoiceTotalAmount;
      const invoiceOutstandingAmount = isIncomeUpload
        ? roundToTwoDecimals(Math.max(invoiceTotalAmount - invoiceAmountPaid, 0))
        : 0;
      const invoicePaymentDate = normalizeReceiptDateForInput(receiptMetadata.paymentDate || transactionDate);
      const selectedClient = Array.isArray(clients)
        ? clients.find((client) => client.id === receiptMetadata.clientId)
        : null;
      const selectedClientName = selectedClient
        ? getClientOptionLabel(selectedClient)
        : String(receiptMetadata.clientName || '').trim();
      const receiptClientAllocations = isIncomeUpload
        ? []
        : receiptMetadata.allocateToClient
          ? normalizeClientAllocations(
            Array.isArray(receiptMetadata.clientAllocations) && receiptMetadata.clientAllocations.length > 0
              ? receiptMetadata.clientAllocations
              : (receiptMetadata.clientId ? [{ clientId: receiptMetadata.clientId, clientName: receiptMetadata.clientName }] : []),
            clientById
          )
          : [];
      const primaryReceiptAllocation = receiptClientAllocations[0];

      if (isIncomeUpload && invoicePaymentStatus !== 'paid' && !receiptMetadata.clientId && !selectedClientName) {
        onError('Select a client before saving an unpaid or part paid invoice.');
        return;
      }

      if (isIncomeUpload && invoicePaymentStatus === 'part_paid') {
        if (invoiceAmountPaid <= 0) {
          onError('Enter the amount already paid for this part paid invoice.');
          return;
        }
        if (invoiceAmountPaid - invoiceTotalAmount > 0.01) {
          onError('Amount paid cannot be more than the invoice total.');
          return;
        }
      }

      const sourceItems = Array.isArray(scannedReceipt?.extractedData?.items)
        ? scannedReceipt.extractedData.items
        : Array.isArray(receiptMetadata.items)
          ? receiptMetadata.items
          : [];

      let sanitizedExtractedData = sanitizeReceiptData({
        ...(scannedReceipt?.extractedData || {}),
        date: transactionDate,
        amount: receiptMetadata.amount,
        vendor: receiptMetadata.vendor || scannedReceipt?.extractedData?.vendor || '',
        category: normalizeCategoryId(receiptMetadata.category || scannedReceipt?.extractedData?.category || 'supplies'),
        suggestedCategoryName: receiptMetadata.suggestedCategoryName || scannedReceipt?.extractedData?.suggestedCategoryName || '',
        notes: receiptMetadata.notes || '',
        vatAmount: receiptMetadata.vatAmount,
        vatRate: receiptMetadata.vatRate,
        vatComponents: receiptMetadata.vatComponents,
        items: sourceItems,
        currency: receiptMetadata.currency || scannedReceipt?.extractedData?.currency || defaultCurrencySymbol
      });
      sanitizedExtractedData = applyReceiptTextCorrections(
        sanitizedExtractedData,
        scannedReceipt?.rawText || scannedReceipt?.text || scannedReceipt?.fullText || ''
      );

      const vatRate = sanitizedExtractedData.vatRate ?? null;
      const vatComponents = Array.isArray(sanitizedExtractedData.vatComponents)
        ? sanitizedExtractedData.vatComponents
        : [];
      const safeItems = Array.isArray(sanitizedExtractedData.items) ? sanitizedExtractedData.items : [];
      let receiptRecordForTransaction = scannedReceipt
        ? {
            ...scannedReceipt,
            extractedData: sanitizedExtractedData
          }
        : scannedReceipt;

      let persistedReceiptId =
        getPersistedReceiptId(scannedReceipt) ||
        getPersistedReceiptId({ id: scannedReceipt?.receiptId }) ||
        null;
      let hasPersistedFinalMetadata = false;

      const applyPersistedReceiptToState = (persistedId, createdReceipt = null) => {
        if (!persistedId) return;

        receiptRecordForTransaction = {
          ...(receiptRecordForTransaction || {}),
          ...(createdReceipt || {}),
          id: persistedId,
          receiptId: persistedId,
          extractedData: sanitizedExtractedData
        };

        setScannedReceipt((prev) => (
          prev
            ? {
                ...prev,
                id: persistedId,
                receiptId: persistedId,
                extractedData: sanitizedExtractedData
              }
            : prev
        ));
      };

      const createPersistedReceiptRecord = async () => {
        if (!auth.currentUser) return null;
        try {
          const rawImageUrl = scannedReceipt?.imageUrl || scannedReceipt?.receiptImageUrl || '';
          const imageUrl =
            typeof rawImageUrl === 'string' && /^https?:\/\//i.test(rawImageUrl)
              ? rawImageUrl
              : '';

          const createdReceipt = await createReceipt({
            imageUrl,
            status: scannedReceipt?.status || 'processed',
            scanStatus: scannedReceipt?.scanStatus || scannedReceipt?.status || 'processed',
            extractedData: sanitizedExtractedData
          }, { workspace: activeWorkspace });

          const createdId = getPersistedReceiptId(createdReceipt);
          if (!createdId) return null;

          persistedReceiptId = createdId;
          notifyReceiptCommitted({ id: createdId });
          applyPersistedReceiptToState(createdId, createdReceipt);
          return createdId;
        } catch (createError) {
          console.warn('Failed to create receipt record before transaction save:', createError);
          return null;
        }
      };

      // Ensure we always persist a Firestore receipt record when user explicitly saves.
      if (auth.currentUser && persistedReceiptId) {
        try {
          await updateReceipt(persistedReceiptId, { extractedData: sanitizedExtractedData }, { workspace: activeWorkspace });
          applyPersistedReceiptToState(persistedReceiptId);
          hasPersistedFinalMetadata = true;
        } catch (persistError) {
          console.warn(
            'Existing receipt reference could not be updated. Creating a new receipt record instead:',
            persistError
          );
          persistedReceiptId = null;
        }
      }

      if (auth.currentUser && !persistedReceiptId) {
        await createPersistedReceiptRecord();
      }

      if (persistedReceiptId) {
        applyPersistedReceiptToState(persistedReceiptId);
      }

      // Persist final normalized receipt data so receipt list/detail stay sane.
      if (persistedReceiptId && auth.currentUser && !hasPersistedFinalMetadata) {
        try {
          await updateReceipt(persistedReceiptId, { extractedData: sanitizedExtractedData }, { workspace: activeWorkspace });
          applyPersistedReceiptToState(persistedReceiptId);
        } catch (persistError) {
          console.warn('Failed to persist final receipt metadata before transaction save:', persistError);
        }
      }

      const fallbackCategory = normalizeCategoryId(
        receiptMetadata.category || sanitizedExtractedData.category || (isIncomeUpload ? categoryOptions[0]?.id : 'supplies')
      );
      const incomeItemPaymentRatio =
        isIncomeUpload && invoiceTotalAmount > 0
          ? Math.min(Math.max(invoiceAmountPaid / invoiceTotalAmount, 0), 1)
          : 1;
      const normalizedItemsForSingleTransaction = safeItems.map((item, index) => {
        const itemAmountRaw = parseAmountNumber(item?.amount ?? item?.total);
        const itemAmount = itemAmountRaw === null ? 0 : roundToTwoDecimals(itemAmountRaw);
        const itemCategory = normalizeCategoryId(item?.category || '');
        const scannerFallbackCategory = normalizeCategoryId(isIncomeUpload ? categoryOptions[0]?.id : expenseCategoryOptions[0]?.id || 'supplies');
        const shouldUseFallbackCategory =
          !itemCategory ||
          itemCategory === scannerFallbackCategory;
        return {
          ...item,
          description: item?.description || `Item ${index + 1}`,
          category: shouldUseFallbackCategory ? fallbackCategory : itemCategory,
          amount: isIncomeUpload ? roundToTwoDecimals(itemAmount * incomeItemPaymentRatio) : itemAmount,
          invoiceAmount: isIncomeUpload ? itemAmount : undefined
        };
      });

      const singleTransactionCategoryBreakdown = Object.values(
        normalizedItemsForSingleTransaction.reduce((acc, item) => {
          const itemCategory = normalizeCategoryId(item?.category || fallbackCategory);
          if (!acc[itemCategory]) {
            acc[itemCategory] = { category: itemCategory, amount: 0 };
          }
          acc[itemCategory].amount += Number(item?.amount) || 0;
          return acc;
        }, {})
      ).map((entry) => ({
        ...entry,
        amount: roundToTwoDecimals(entry.amount)
      }));

      const dominantSingleTransactionCategory = [...singleTransactionCategoryBreakdown]
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))[0]?.category;
      const effectiveVatAmount = isIncomeUpload && sanitizedExtractedData.vatAmount !== null && sanitizedExtractedData.vatAmount !== undefined
        ? roundToTwoDecimals((parseAmountNumber(sanitizedExtractedData.vatAmount) || 0) * incomeItemPaymentRatio)
        : sanitizedExtractedData.vatAmount ?? null;
      const effectiveVatComponents = isIncomeUpload && Array.isArray(vatComponents)
        ? vatComponents.map((component) => ({
            ...component,
            amount: roundToTwoDecimals((parseAmountNumber(component?.amount) || 0) * incomeItemPaymentRatio)
          }))
        : vatComponents;
      const isUnpaidExpenseUpload = !isIncomeUpload && receiptMetadata.paymentMethod === 'unpaid';
      const expenseInvoiceTotalAmount = sanitizedExtractedData.amount ?? 0;
      const isRctExpenseUpload = supportsRct && !isIncomeUpload && normalizeCategoryId(receiptMetadata.category) === 'subcontractor_rct';
      const rctDeductionRate = [0, 0.2, 0.35].includes(Number(receiptMetadata.rctDeductionRate))
        ? Number(receiptMetadata.rctDeductionRate)
        : 0.2;
      const rctDeductionAmount = isRctExpenseUpload ? roundToTwoDecimals(expenseInvoiceTotalAmount * rctDeductionRate) : 0;
      const rctNetPayable = isRctExpenseUpload ? roundToTwoDecimals(Math.max(expenseInvoiceTotalAmount - rctDeductionAmount, 0)) : expenseInvoiceTotalAmount;
      const rctReverseChargeVatAmount = isRctExpenseUpload ? roundToTwoDecimals(expenseInvoiceTotalAmount * 0.135) : 0;
      const expenseAmountPaid = isUnpaidExpenseUpload ? 0 : rctNetPayable;

      const singleTransactionOptions = {
        amount: isIncomeUpload ? invoiceAmountPaid : expenseAmountPaid,
        vatAmount: isUnpaidExpenseUpload ? 0 : effectiveVatAmount,
        vatRate,
        vatComponents: isUnpaidExpenseUpload ? [] : effectiveVatComponents,
        currency: sanitizedExtractedData.currency || defaultCurrencySymbol,
        items: normalizedItemsForSingleTransaction,
        category: dominantSingleTransactionCategory || fallbackCategory,
        categoryBreakdown: singleTransactionCategoryBreakdown,
        isSplitTransaction:
          normalizedItemsForSingleTransaction.length > 1 ||
          singleTransactionCategoryBreakdown.length > 1,
        receiptRecord: receiptRecordForTransaction,
        extraFields: isIncomeUpload
          ? {
              type: 'income',
              invoiceDate: transactionDate,
              paymentDate: invoicePaymentStatus === 'unpaid' ? '' : invoicePaymentDate,
              paymentMethod: receiptMetadata.paymentMethod || 'card',
              paymentStatus: invoicePaymentStatus,
              invoiceTotalAmount,
              invoiceVatAmount: sanitizedExtractedData.vatAmount ?? null,
              invoiceVatRate: vatRate,
              invoiceVatComponents: Array.isArray(vatComponents) ? vatComponents : [],
              saleTotalAmount: invoiceTotalAmount,
              grossAmount: invoiceAmountPaid,
              amountPaid: invoiceAmountPaid,
              outstandingAmount: invoiceOutstandingAmount,
              isPartPaymentSale: invoiceOutstandingAmount > 0.01,
              isSplitPayment: invoiceOutstandingAmount > 0.01,
              paymentBreakdown: invoiceAmountPaid > 0
                ? [{
                    method: receiptMetadata.paymentMethod || 'card',
                    amount: invoiceAmountPaid,
                    paidAt: invoicePaymentDate
                  }]
                : [],
              clientId: receiptMetadata.clientId || '',
              clientName: selectedClientName
            }
          : {
              type: 'expenses',
              invoiceDate: transactionDate,
              paymentDate: isUnpaidExpenseUpload ? '' : (receiptMetadata.paymentDate || transactionDate),
              paymentMethod: receiptMetadata.paymentMethod || 'card',
              paymentStatus: isUnpaidExpenseUpload ? 'unpaid' : 'paid',
              supplierId: receiptMetadata.supplierId || '',
              supplierName: receiptMetadata.supplierName || receiptMetadata.vendor || '',
              invoiceTotalAmount: expenseInvoiceTotalAmount,
              expenseTotalAmount: expenseInvoiceTotalAmount,
              billTotalAmount: expenseInvoiceTotalAmount,
              invoiceVatAmount: sanitizedExtractedData.vatAmount ?? null,
              invoiceVatRate: vatRate,
              invoiceVatComponents: Array.isArray(vatComponents) ? vatComponents : [],
              grossAmount: expenseAmountPaid,
              amountPaid: expenseAmountPaid,
              outstandingAmount: isUnpaidExpenseUpload ? expenseInvoiceTotalAmount : 0,
              isPartPaymentExpense: isUnpaidExpenseUpload,
              rctRole: isRctExpenseUpload ? 'principal' : '',
              rctReverseCharge: isRctExpenseUpload,
              rctSubcontractorId: isRctExpenseUpload ? (receiptMetadata.rctSubcontractorId || '') : '',
              rctSubcontractorName: isRctExpenseUpload ? (receiptMetadata.rctSubcontractorName || '') : '',
              rctDeductionRate: isRctExpenseUpload ? rctDeductionRate : null,
              rctGrossAmount: isRctExpenseUpload ? expenseInvoiceTotalAmount : null,
              rctDeductionAmount: isRctExpenseUpload ? rctDeductionAmount : null,
              rctNetPayable: isRctExpenseUpload ? rctNetPayable : null,
              rctTaxDueAmount: isRctExpenseUpload ? rctDeductionAmount : null,
              rctReverseChargeVatRate: isRctExpenseUpload ? 0.135 : null,
              rctReverseChargeVatAmount: isRctExpenseUpload ? rctReverseChargeVatAmount : null,
              rctVatOutputAmount: isRctExpenseUpload ? rctReverseChargeVatAmount : null,
              rctVatInputAmount: isRctExpenseUpload ? rctReverseChargeVatAmount : null,
              allocateToClient: receiptClientAllocations.length > 0,
              clientId: receiptClientAllocations.length > 0 ? (primaryReceiptAllocation?.clientId || '') : '',
              clientName: receiptClientAllocations.length > 0 ? (primaryReceiptAllocation?.clientName || '') : '',
              clientAllocations: receiptClientAllocations,
              jobReference: receiptMetadata.allocateToClient ? receiptMetadata.jobReference || '' : '',
              billableToClient: receiptClientAllocations.length > 0 ? !!receiptMetadata.billableToClient : false
            }
      };
      
      // Check if we have line items to create multiple transactions
      if (isIncomeUpload && safeItems.length > 0) {
        createSingleTransaction(invoicePaymentStatus === 'unpaid' ? transactionDate : invoicePaymentDate, vatRate, vatComponents, singleTransactionOptions);
      } else if (safeItems.length > 0) {
        console.log('Creating multiple transactions from line items:', safeItems);
        
        // Create a transaction for each line item
        const transactions = safeItems
          .filter(item => item.description || item.amount)
          .map((item, index) => {
            const keepReceiptScannerOpen = bulkReceiptQueue.length > 1 && bulkReceiptIndex < bulkReceiptQueue.length - 1;
            const itemVatRate = parseVatRateNumber(item.vatRate) ?? vatRate;
            const itemAmountRaw = parseAmountNumber(item.amount);
            const itemAmountNum = itemAmountRaw === null ? 0 : roundToTwoDecimals(itemAmountRaw);
            
            let itemVatAmount = parseAmountNumber(item.vatAmount);
            if (
              itemVatAmount !== null &&
              Math.abs(itemAmountNum) > 0 &&
              Math.abs(itemVatAmount) > Math.abs(itemAmountNum)
            ) {
              itemVatAmount = null;
            }

            const normalizedItemVatAmount =
              itemVatAmount === null ? null : roundToTwoDecimals(itemVatAmount);
            const itemVatComponent =
              itemVatRate !== null && normalizedItemVatAmount !== null
                ? {
                    rate: itemVatRate,
                    amount: normalizedItemVatAmount,
                    description: `${(itemVatRate * 100).toFixed(0)}% VAT`
                  }
                : null;
            const itemCategory = normalizeCategoryId(item.category || receiptMetadata.category || 'supplies');
            
            return {
              date: transactionDate,
              amount: isUnpaidExpenseUpload ? 0 : itemAmountNum,
              description: `${receiptMetadata.vendor} - ${item.description || `Item ${index + 1}`}`,
              category: itemCategory,
              vatAmount: isUnpaidExpenseUpload ? 0 : normalizedItemVatAmount,
              vatRate: itemVatRate,
              vatComponents: isUnpaidExpenseUpload ? [] : (itemVatComponent ? [itemVatComponent] : []),
              currency: sanitizedExtractedData.currency || defaultCurrencySymbol,
              notes: `${receiptMetadata.notes || ''} ${item.description ? `Item: ${item.description}` : ''}`.trim(),
              items: [{
                ...item,
                amount: isUnpaidExpenseUpload ? 0 : itemAmountNum,
                invoiceAmount: itemAmountNum,
                invoiceVatAmount: normalizedItemVatAmount
              }],
              type: 'expenses',
              invoiceDate: transactionDate,
              paymentDate: isUnpaidExpenseUpload ? '' : transactionDate,
              paymentMethod: receiptMetadata.paymentMethod || 'card',
              paymentStatus: isUnpaidExpenseUpload ? 'unpaid' : 'paid',
              supplierId: receiptMetadata.supplierId || '',
              supplierName: receiptMetadata.supplierName || receiptMetadata.vendor || '',
              invoiceTotalAmount: itemAmountNum,
              expenseTotalAmount: itemAmountNum,
              billTotalAmount: itemAmountNum,
              invoiceVatAmount: normalizedItemVatAmount,
              invoiceVatRate: itemVatRate,
              invoiceVatComponents: itemVatComponent ? [itemVatComponent] : [],
              grossAmount: isUnpaidExpenseUpload ? 0 : itemAmountNum,
              amountPaid: isUnpaidExpenseUpload ? 0 : itemAmountNum,
              outstandingAmount: isUnpaidExpenseUpload ? itemAmountNum : 0,
              isPartPaymentExpense: isUnpaidExpenseUpload,
              allocateToClient: receiptClientAllocations.length > 0,
              clientId: receiptClientAllocations.length > 0 ? (primaryReceiptAllocation?.clientId || '') : '',
              clientName: receiptClientAllocations.length > 0 ? (primaryReceiptAllocation?.clientName || '') : '',
              clientAllocations: receiptClientAllocations,
              jobReference: receiptMetadata.allocateToClient ? receiptMetadata.jobReference || '' : '',
              billableToClient: receiptClientAllocations.length > 0 ? !!receiptMetadata.billableToClient : false,
              receiptId: receiptRecordForTransaction?.id,
              receiptImageUrl: receiptRecordForTransaction?.imageUrl,
              timestamp: new Date().getTime() + index,
              keepReceiptScannerOpen
            };
          });
        
        if (transactions.length > 0) {
          const uniqueCategories = [
            ...new Set(transactions.map((transaction) => transaction.category).filter(Boolean))
          ];
          const hasSingleCategoryAcrossItems = uniqueCategories.length <= 1;

          if (hasSingleCategoryAcrossItems && transactions.length > 1) {
            const splitChoice = window.prompt(
              `This receipt contains ${transactions.length} line items in one category.\nChoose how to save:\n1 = Create ${transactions.length} separate transactions\n2 = Save one transaction with line items\n\nEnter 1 or 2:`,
              '2'
            );
            const normalizedChoice = String(splitChoice || '').trim();
            const createMultiple = normalizedChoice === '1';

            if (createMultiple) {
              transactions.forEach((transaction) => {
                onReceiptProcessed(transaction);
              });
            } else {
              createSingleTransaction(transactionDate, vatRate, vatComponents, singleTransactionOptions);
            }
          } else {
            // Default behavior: save one transaction with line items/category split.
            createSingleTransaction(transactionDate, vatRate, vatComponents, singleTransactionOptions);
          }
        } else {
          createSingleTransaction(transactionDate, vatRate, vatComponents, singleTransactionOptions);
        }
      } else {
        createSingleTransaction(transactionDate, vatRate, vatComponents, singleTransactionOptions);
      }

      if (bulkReceiptQueue.length > 1 && bulkReceiptIndex < bulkReceiptQueue.length - 1) {
        loadBulkReceiptForReview(bulkReceiptIndex + 1);
      } else {
        setBulkReceiptQueue([]);
        setBulkReceiptIndex(0);
        resetForm({ preserveReceiptRecord: true });
      }
    } catch (error) {
      console.error('Failed to convert receipt to transaction:', error);
      onError('Failed to process receipt. Please try again.');
    }
  };
  
  /**
  * Create a single transaction from receipt data
  */
  const createSingleTransaction = (transactionDate, vatRate, vatComponents, options = {}) => {
    const {
      amount = parseAmountNumber(receiptMetadata.amount) || 0,
      vatAmount = parseAmountNumber(receiptMetadata.vatAmount),
      vatComponents: optionVatComponents = vatComponents,
      currency = receiptMetadata.currency || defaultCurrencySymbol,
      items = scannedReceipt && scannedReceipt.extractedData && scannedReceipt.extractedData.items ? scannedReceipt.extractedData.items : [],
      category = normalizeCategoryId(receiptMetadata.category || 'supplies'),
      categoryBreakdown = [],
      isSplitTransaction = (Array.isArray(items) && items.length > 1),
      receiptRecord = scannedReceipt,
      extraFields = {}
    } = options;
    const keepReceiptScannerOpen = bulkReceiptQueue.length > 1 && bulkReceiptIndex < bulkReceiptQueue.length - 1;

    const transaction = {
      date: transactionDate,
      amount,
      description: receiptMetadata.vendor,
      category,
      vatAmount,
      vatRate: vatRate ?? null,
      vatComponents: Array.isArray(optionVatComponents) ? optionVatComponents : [],
      currency,
      notes: receiptMetadata.notes,
      items,
      categoryBreakdown: Array.isArray(categoryBreakdown) ? categoryBreakdown : [],
      isSplitTransaction: Boolean(isSplitTransaction),
      type: 'expenses',
      ...extraFields,
      receiptId: receiptRecord?.id,
      receiptImageUrl: receiptRecord?.imageUrl,
      timestamp: new Date().getTime(),
      keepReceiptScannerOpen
    };
    
    console.log('Created single transaction from receipt:', transaction);
    onReceiptProcessed(transaction);
  };
  
  /**
   * Reset the form
   */
  const resetForm = (options = {}) => {
    const { preserveReceiptRecord = false } = options;
    if (!preserveReceiptRecord) {
      notifyReceiptAbandoned(scannedReceipt);
    }

    setScannedReceipt(null);
    setBulkReceiptQueue([]);
    setBulkReceiptIndex(0);
    setReceiptMetadata(getInitialMetadata());
    setActiveTab('details');
    unlockForm();
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  /**
   * Render the component
   */
  const renderComponent = () => {
    // Get items from scanned receipt if available
    const items = scannedReceipt && 
                  scannedReceipt.extractedData && 
                  scannedReceipt.extractedData.items ? 
                  scannedReceipt.extractedData.items : [];
    const canEditVat = vatSettings.enabled && vatSettings.isRegistered;
    const hasVatData =
      receiptMetadata.vatAmount !== '' ||
      receiptMetadata.vatRate !== '' ||
      (Array.isArray(receiptMetadata.vatComponents) && receiptMetadata.vatComponents.length > 0);
    
    // If we're uploading, show the upload progress
    if (isUploading) {
      return (
        <div className="p-4 bg-white rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {isIncomeUpload ? 'Scanning Sales Invoice' : 'Scanning Receipt'}
          </h2>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {processingStatus || (uploadProgress < 100 ? 'Processing your receipt...' : 'Almost done...')}
          </p>
        </div>
      );
    }
    
    // If we have a scanned receipt, show the receipt details
    if (scannedReceipt) {
      return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Tabs for mobile view */}
          <div className="md:hidden border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('details')}
                className={`w-1/3 py-3 px-1 text-center border-b-2 text-sm font-medium ${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('items')}
                className={`w-1/3 py-3 px-1 text-center border-b-2 text-sm font-medium ${
                  activeTab === 'items'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Items {items.length > 0 && `(${items.length})`}
              </button>
              <button
                onClick={() => setActiveTab('vat')}
                className={`w-1/3 py-3 px-1 text-center border-b-2 text-sm font-medium ${
                  activeTab === 'vat'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {taxName}
              </button>
            </nav>
          </div>
          
          <div className="p-4">
            {bulkReceiptQueue.length > 1 && (
              <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-indigo-900">
                      Receipt {bulkReceiptIndex + 1} of {bulkReceiptQueue.length}
                    </p>
                    <p className="text-xs text-indigo-700">
                      Multiple receipts were detected from this upload. Review each one or save the whole batch.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => loadBulkReceiptForReview(Math.max(0, bulkReceiptIndex - 1))}
                      disabled={bulkReceiptIndex === 0}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-indigo-200 text-indigo-700 bg-white disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => loadBulkReceiptForReview(Math.min(bulkReceiptQueue.length - 1, bulkReceiptIndex + 1))}
                      disabled={bulkReceiptIndex >= bulkReceiptQueue.length - 1}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-indigo-200 text-indigo-700 bg-white disabled:opacity-50"
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={saveAllBulkReceipts}
                      className="px-3 py-1.5 text-xs font-medium rounded border border-transparent text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      Save All
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Receipt image */}
            {scannedReceipt.imageUrl && (
              <div className={`mb-4 ${activeTab !== 'details' && 'md:block hidden'}`}>
                <div className="aspect-w-4 aspect-h-5 rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={scannedReceipt.imageUrl} 
                    alt={isIncomeUpload ? 'Scanned sales invoice' : 'Scanned receipt'} 
                    className="object-contain w-full h-full"
                  />
                </div>
              </div>
            )}
            
            {/* Receipt details form */}
            <div className={`${activeTab !== 'details' && 'md:block hidden'}`}>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isIncomeUpload ? 'Invoice / Receipt Details' : 'Receipt Details'}
              </h3>
              
              {/* Debug info - can be removed later */}
              {process.env.NODE_ENV !== 'production' && (
                <div className="bg-blue-50 p-2 mb-4 rounded text-xs font-mono">
                  <details>
                    <summary className="cursor-pointer">Debug Receipt Data</summary>
                    <pre>Date: {receiptMetadata.date}</pre>
                    <pre>Amount: {receiptMetadata.amount}</pre>
                    <pre>Vendor: {receiptMetadata.vendor}</pre>
                    <pre>Category: {receiptMetadata.category}</pre>
                    <pre>Force Update: {forceUpdate}</pre>
                  </details>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={receiptMetadata.date}
                    onChange={(e) => handleMetadataChange('date', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                    {isIncomeUpload ? 'Invoice Total' : 'Total Amount'}
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {receiptMetadata.currency || defaultCurrencySymbol}
                      </span>
                    </div>
                    <input
                      type="text"
                      id="amount"
                      value={receiptMetadata.amount}
                      onChange={(e) => handleMetadataChange('amount', e.target.value)}
                      className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="0.00"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">
                        {receiptMetadata.currency || defaultCurrencySymbol}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-1">
                    {isIncomeUpload ? 'Client / Payer shown on document' : 'Vendor'}
                  </label>
                  <input
                    type="text"
                    id="vendor"
                    value={receiptMetadata.vendor}
                    onChange={(e) => handleMetadataChange('vendor', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder={isIncomeUpload ? 'Client or payer name' : 'Vendor name'}
                  />
                </div>
                
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    value={receiptMetadata.category}
                    onChange={(e) => handleMetadataChange('category', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select a category</option>
                    {categoryOptions.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {getCategoryIcon(id)} {name}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const suggestion = String(receiptMetadata.suggestedCategoryName || '').trim();
                    if (!suggestion) return null;
                    const normalizedSuggestionId = normalizeCategoryId(suggestion);
                    const alreadyExists = categoryOptions.some(
                      (category) =>
                        String(category?.name || '').trim().toLowerCase() === suggestion.toLowerCase() ||
                        normalizeCategoryId(category?.id || category?.name || '') === normalizedSuggestionId
                    );
                    if (alreadyExists || !onAddCategory) return null;

                    return (
                      <button
                        type="button"
                        onClick={() => {
                          const createdCategoryId = onAddCategory(normalizedTransactionType, suggestion);
                          if (createdCategoryId) {
                            handleMetadataChange('category', normalizeCategoryId(createdCategoryId));
                            handleMetadataChange('suggestedCategoryName', '');
                          }
                        }}
                        className="mt-2 mr-3 text-xs text-[color:var(--color-primary)] hover:underline"
                      >
                        Suggested: + Add "{suggestion}"
                      </button>
                    );
                  })()}
                  {onAddCategory && (
                    <button
                      type="button"
                      onClick={() => onAddCategory(normalizedTransactionType)}
                      className="mt-2 text-xs text-[color:var(--color-text)] hover:underline"
                    >
                      + New category
                    </button>
                  )}
                </div>

                <div>
                  <label htmlFor={isIncomeUpload ? 'clientId' : undefined} className="block text-sm font-medium text-gray-700 mb-1">
                    {isIncomeUpload ? 'Link to Client' : 'Allocate to Client'}
                  </label>
                  {isIncomeUpload ? (
                    <select
                      id="clientId"
                      value={receiptMetadata.clientId || ''}
                      onChange={(e) => {
                        const client = clients.find((entry) => entry.id === e.target.value);
                        handleMetadataChange('clientId', e.target.value);
                        handleMetadataChange('clientName', client ? getClientOptionLabel(client) : '');
                        handleMetadataChange('clientAllocations', []);
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="">Select client for outstanding balance</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {getClientOptionLabel(client)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <details className="relative">
                      <summary className="flex w-full cursor-pointer list-none items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                        <span className="truncate">{getExpenseAllocationLabel(receiptMetadata) || 'No client allocation'}</span>
                        <span className="text-gray-400">v</span>
                      </summary>
                      <div className="absolute z-30 mt-1 w-full rounded-md border border-gray-200 bg-white p-3 shadow-lg">
                        <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                          {clients.length > 0 ? clients.map((client) => {
                            const selectedIds = getExpenseAllocationIds(receiptMetadata);
                            const isSelected = selectedIds.includes(client.id);
                            return (
                              <label key={client.id} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const previousIds = getExpenseAllocationIds(receiptMetadata);
                                    const nextIds = e.target.checked
                                      ? [...previousIds, client.id]
                                      : previousIds.filter((clientId) => clientId !== client.id);
                                    const clientAllocations = buildClientAllocationsFromIds(nextIds);
                                    const primaryAllocation = clientAllocations[0];
                                    handleMetadataChange('clientId', primaryAllocation?.clientId || '');
                                    handleMetadataChange('clientName', primaryAllocation?.clientName || '');
                                    handleMetadataChange('clientAllocations', clientAllocations);
                                    handleMetadataChange('allocateToClient', clientAllocations.length > 0);
                                  }}
                                />
                                <span>{getClientOptionLabel(client)}</span>
                              </label>
                            );
                          }) : (
                            <div className="text-sm text-gray-500">No clients yet.</div>
                          )}
                        </div>
                        {getExpenseAllocationIds(receiptMetadata).length > 1 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Split equally across {getExpenseAllocationIds(receiptMetadata).length} clients.
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                  {onAddClient && (
                    <button
                      type="button"
                      onClick={handleQuickAddClient}
                      className="mt-2 text-xs text-[color:var(--color-text)] hover:underline"
                    >
                      + New client
                    </button>
                  )}
                </div>

                {isIncomeUpload ? (
                  <>
                    <div>
                      <label htmlFor="paymentStatus" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Status
                      </label>
                      <select
                        id="paymentStatus"
                        value={receiptMetadata.paymentStatus || 'paid'}
                        onChange={(e) => handleMetadataChange('paymentStatus', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="paid">Paid in full</option>
                        <option value="part_paid">Part paid / deposit paid</option>
                        <option value="unpaid">Unpaid invoice</option>
                      </select>
                    </div>

                    {receiptMetadata.paymentStatus !== 'unpaid' && (
                      <>
                        <div>
                          <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700 mb-1">
                            Amount Paid
                          </label>
                          <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">
                                {receiptMetadata.currency || defaultCurrencySymbol}
                              </span>
                            </div>
                            <input
                              type="text"
                              id="amountPaid"
                              value={receiptMetadata.paymentStatus === 'paid' ? receiptMetadata.amount : receiptMetadata.amountPaid}
                              onChange={(e) => handleMetadataChange('amountPaid', e.target.value)}
                              disabled={receiptMetadata.paymentStatus === 'paid'}
                              className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Date
                          </label>
                          <input
                            type="date"
                            id="paymentDate"
                            value={receiptMetadata.paymentDate || receiptMetadata.date}
                            onChange={(e) => handleMetadataChange('paymentDate', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                            Payment Method
                          </label>
                          <select
                            id="paymentMethod"
                            value={receiptMetadata.paymentMethod || 'card'}
                            onChange={(e) => handleMetadataChange('paymentMethod', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          >
                            {paymentMethods.map((method) => (
                              <option key={method.id} value={method.id}>
                                {method.name || method.id}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label htmlFor="supplierId" className="block text-sm font-medium text-gray-700 mb-1">
                        Supplier
                      </label>
                      <select
                        id="supplierId"
                        value={receiptMetadata.supplierId || ''}
                        onChange={(e) => {
                          const selected = supplierOptions.find((entry) => entry.id === e.target.value);
                          handleMetadataChange('supplierId', selected?.id || '');
                          handleMetadataChange('supplierName', selected?.name || '');
                          if (selected?.name) {
                            handleMetadataChange('vendor', selected.name);
                          }
                          if (selected?.supplierType === 'rct') {
                            handleMetadataChange('rctSubcontractorId', selected.id);
                            handleMetadataChange('rctSubcontractorName', selected.name);
                            handleMetadataChange('rctDeductionRate', selected.rctDeductionRate ?? 0.2);
                            handleMetadataChange('rctReverseCharge', true);
                          }
                        }}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">No supplier selected</option>
                        {supplierOptions.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name} {supportsRct && supplier.supplierType === 'rct' ? `(RCT ${Math.round((supplier.rctDeductionRate || 0) * 100)}%)` : ''}
                          </option>
                        ))}
                      </select>
                      {onAddSupplier && (
                        <button
                          type="button"
                          onClick={handleQuickAddSupplier}
                          className="mt-2 text-xs text-[color:var(--color-text)] hover:underline"
                        >
                          + New supplier
                        </button>
                      )}
                    </div>

                    <div>
                      <label htmlFor="expensePaymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method
                      </label>
                      <select
                        id="expensePaymentMethod"
                        value={receiptMetadata.paymentMethod || 'card'}
                        onChange={(e) => handleMetadataChange('paymentMethod', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        {paymentMethods.map((method) => (
                          <option key={method.id} value={method.id}>
                            {method.name || method.id}
                          </option>
                        ))}
                      </select>
                    </div>

                    {receiptMetadata.paymentMethod !== 'unpaid' && (
                      <div>
                        <label htmlFor="expensePaymentDate" className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Date
                        </label>
                        <input
                          type="date"
                          id="expensePaymentDate"
                          value={receiptMetadata.paymentDate || receiptMetadata.date}
                          onChange={(e) => handleMetadataChange('paymentDate', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    )}

                    {supportsRct && normalizeCategoryId(receiptMetadata.category) === 'subcontractor_rct' && (
                      <div className="md:col-span-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label htmlFor="rctSubcontractorId" className="block text-sm font-medium text-gray-700 mb-1">
                              RCT Subcontractor
                            </label>
                            <select
                              id="rctSubcontractorId"
                              value={receiptMetadata.rctSubcontractorId || ''}
                              onChange={(e) => {
                                const selected = rctSubcontractors.find((entry) => entry.id === e.target.value);
                                handleMetadataChange('rctSubcontractorId', selected?.id || '');
                                handleMetadataChange('rctSubcontractorName', selected?.name || receiptMetadata.rctSubcontractorName || '');
                                handleMetadataChange('rctDeductionRate', selected?.rctDeductionRate ?? receiptMetadata.rctDeductionRate ?? 0.2);
                                handleMetadataChange('rctReverseCharge', true);
                              }}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                              <option value="">Select subcontractor</option>
                              {rctSubcontractors.filter((subcontractor) => subcontractor.supplierType === 'rct').map((subcontractor) => (
                                <option key={subcontractor.id} value={subcontractor.id}>
                                  {subcontractor.name} ({Math.round((subcontractor.rctDeductionRate || 0) * 100)}%)
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="rctSubcontractorName" className="block text-sm font-medium text-gray-700 mb-1">
                              Subcontractor Name
                            </label>
                            <input
                              type="text"
                              id="rctSubcontractorName"
                              value={receiptMetadata.rctSubcontractorName || ''}
                              onChange={(e) => {
                                handleMetadataChange('rctSubcontractorName', e.target.value);
                                handleMetadataChange('rctReverseCharge', true);
                              }}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              placeholder="Subcontractor name"
                            />
                          </div>
                          <div>
                            <label htmlFor="rctDeductionRate" className="block text-sm font-medium text-gray-700 mb-1">
                              RCT Deduction
                            </label>
                            <select
                              id="rctDeductionRate"
                              value={String(receiptMetadata.rctDeductionRate ?? 0.2)}
                              onChange={(e) => {
                                handleMetadataChange('rctDeductionRate', Number(e.target.value));
                                handleMetadataChange('rctReverseCharge', true);
                              }}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                              <option value="0">0%</option>
                              <option value="0.2">20%</option>
                              <option value="0.35">35%</option>
                            </select>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-600">
                          This records the reverse-charge VAT and splits the subcontractor invoice between net pay and RCT due to Revenue.
                        </p>
                      </div>
                    )}

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!receiptMetadata.allocateToClient}
                        onChange={(e) => {
                          handleMetadataChange('allocateToClient', e.target.checked);
                          if (!e.target.checked) {
                            handleMetadataChange('clientId', '');
                            handleMetadataChange('clientName', '');
                            handleMetadataChange('clientAllocations', []);
                            handleMetadataChange('billableToClient', false);
                          }
                        }}
                      />
                      {supportsProjects ? 'Allocate this purchase to a client or job' : 'Allocate this purchase to a client'}
                    </label>
                    {receiptMetadata.allocateToClient && (
                      <>
                        {supportsProjects && (
                          <div>
                            <label htmlFor="jobReference" className="block text-sm font-medium text-gray-700 mb-1">
                              Job / Estimate Reference
                            </label>
                            <input
                              type="text"
                              id="jobReference"
                              value={receiptMetadata.jobReference || ''}
                              onChange={(e) => handleMetadataChange('jobReference', e.target.value)}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              placeholder="e.g. Smith hallway repaint"
                            />
                          </div>
                        )}
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!receiptMetadata.billableToClient}
                            onChange={(e) => handleMetadataChange('billableToClient', e.target.checked)}
                          />
                          Mark as billable / rechargeable to this client
                        </label>
                      </>
                    )}
                  </>
                )}
                
                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    id="currency"
                    value={receiptMetadata.currency}
                    onChange={(e) => handleMetadataChange('currency', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="€">Euro (€)</option>
                    <option value="$">US Dollar ($)</option>
                    <option value="£">British Pound (£)</option>
                    <option value="¥">Japanese Yen (¥)</option>
                    <option value="₩">Korean Won (₩)</option>
                    <option value="₹">Indian Rupee (₹)</option>
                    <option value="₽">Russian Ruble (₽)</option>
                    <option value="₺">Turkish Lira (₺)</option>
                    <option value="₴">Ukrainian Hryvnia (₴)</option>
                    <option value="₦">Nigerian Naira (₦)</option>
                    <option value="₱">Philippine Peso (₱)</option>
                    <option value="₲">Paraguayan Guarani (₲)</option>
                    <option value="₡">Costa Rican Colón (₡)</option>
                    <option value="₫">Vietnamese Dong (₫)</option>
                    <option value="฿">Thai Baht (฿)</option>
                    <option value="₭">Lao Kip (₭)</option>
                    <option value="₮">Mongolian Tugrik (₮)</option>
                    <option value="₩">South Korean Won (₩)</option>
                    <option value="₪">Israeli New Shekel (₪)</option>
                    <option value="₨">Pakistani Rupee (₨)</option>
                    <option value="₸">Kazakhstani Tenge (₸)</option>
                    <option value="₼">Azerbaijani Manat (₼)</option>
                    <option value="₾">Georgian Lari (₾)</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    rows="3"
                    value={receiptMetadata.notes}
                    onChange={(e) => handleMetadataChange('notes', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Additional notes about this receipt"
                  ></textarea>
                </div>
              </div>
            </div>
            
            {/* Tax details */}
            <div className={`${activeTab !== 'vat' && 'md:block hidden'}`}>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{taxName} Details</h3>
              
              {/* Debug Info (only visible during development) */}
              {process.env.NODE_ENV !== 'production' && (
                <div className="bg-blue-50 p-2 mb-4 rounded text-xs font-mono">
                  <details>
                    <summary className="cursor-pointer">Debug VAT Data</summary>
                    <pre>VAT Amount: {receiptMetadata.vatAmount}</pre>
                    <pre>VAT Rate: {receiptMetadata.vatRate}</pre>
                    <pre>VAT Components: {JSON.stringify(receiptMetadata.vatComponents, null, 2)}</pre>
                  </details>
                </div>
              )}
              
              {canEditVat || hasVatData ? (
                <div>
                  {/* Total VAT amount */}
                  <div className="mb-4">
                    <div>
                      <label htmlFor="vatAmount" className="block text-sm font-medium text-gray-700 mb-1">
                        Total {taxName} Amount
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">
                            {receiptMetadata.currency || defaultCurrencySymbol}
                          </span>
                        </div>
                        <input
                          type="number"
                          id="vatAmount"
                          step="0.01"
                          min="0"
                          value={receiptMetadata.vatAmount}
                          onChange={(e) => handleMetadataChange('vatAmount', e.target.value)}
                          disabled={!canEditVat}
                          className="block w-full pl-7 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This is the total {taxName} amount for the entire receipt. For receipts with multiple {taxName} rates, add individual components below.
                      </p>
                    </div>
                  </div>
                  
                  {/* Multiple tax components */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-700">{taxName} Components</h4>
                      <button
                        type="button"
                        onClick={addVatComponent}
                        disabled={!canEditVat}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-100"
                      >
                        Add Component
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      For receipts with multiple {taxName} rates (e.g., different rates for different items), add each component separately.
                      The sum of all component amounts should equal the total {taxName} amount above.
                    </p>
                    
                  {receiptMetadata.vatComponents && receiptMetadata.vatComponents.length > 0 ? (
                    <div className="space-y-2">
                        {receiptMetadata.vatComponents.map((component, index) => (
                          <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md">
                            <div className="flex-grow grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Rate</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  value={component.rate}
                                  onChange={(e) => handleMetadataChange('vatComponents', { ...component, rate: e.target.value }, index)}
                                  disabled={!canEditVat}
                                  className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                                  placeholder={defaultVatRateString}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={component.amount}
                                  onChange={(e) => handleMetadataChange('vatComponents', { ...component, amount: e.target.value }, index)}
                                  disabled={!canEditVat}
                                  className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                                  placeholder="0.00"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                                <input
                                  type="text"
                                  value={component.description}
                                  onChange={(e) => handleMetadataChange('vatComponents', { ...component, description: e.target.value }, index)}
                                  disabled={!canEditVat}
                                  className="block w-full px-2 py-1 text-sm border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                                  placeholder="Description"
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVatComponent(index)}
                              disabled={!canEditVat}
                              className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-red-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No {taxName} components added yet.</p>
                  )}
                </div>
                {!canEditVat && (
                  <p className="text-xs text-yellow-700">
                    {taxName} values were extracted from the receipt but are read-only because {taxName} tracking is disabled in settings.
                  </p>
                )}
              </div>
              ) : (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        {!vatSettings.enabled 
                          ? `${taxName} tracking is disabled. Enable it in Tax Calculation settings to track ${taxName} on receipts.`
                          : `${taxName} registration is not enabled. Enable it in Tax Calculation settings to track ${taxName} on receipts.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Line items */}
            <div className={`${activeTab !== 'items' && 'md:block hidden'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
                <button
                  type="button"
                  onClick={handleAddLineItem}
                  className="inline-flex items-center px-2.5 py-1.5 border border-indigo-200 text-xs font-medium rounded text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                >
                  + Add line item
                </button>
              </div>
              
              {items.length === 0 ? (
                <div className="bg-gray-50 p-4 rounded-md text-center">
                  <p className="text-sm text-gray-500">No line items found in this receipt.</p>
                  <button
                    type="button"
                    onClick={handleAddLineItem}
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-indigo-300 text-xs font-medium rounded text-indigo-700 bg-white hover:bg-indigo-50"
                  >
                    Add first line item
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                  {/* Mobile item cards */}
                  <div className="md:hidden space-y-2 p-2">
                    {items.map((item, index) => {
                      // Get the current category for this item and normalize it
                      const itemCategory = normalizeCategoryId(item.category || receiptMetadata.category || 'supplies');
                      
                      // Determine if this is the first item of a new category group (normalize for consistency)
                      const prevItemCategory = index > 0 ? normalizeCategoryId(items[index-1].category || receiptMetadata.category || 'supplies') : null;
                      const isNewCategoryGroup = index === 0 || itemCategory !== prevItemCategory;
                      
                      return (
                      <div 
                        key={index} 
                        className={`p-3 rounded-md ${isNewCategoryGroup ? 'bg-indigo-50 border-l-4 border-indigo-300' : 'bg-gray-50'}`}
                      >
                        {isNewCategoryGroup && (
                          <div className="flex items-center mb-2 text-xs font-medium text-indigo-800 bg-indigo-100 px-2 py-1 rounded">
                            {getCategoryIcon(itemCategory)} {getCategoryDisplayName(itemCategory)}
                          </div>
                        )}
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                          <input
                            type="text"
                            className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={item.description ?? item.name ?? item.item ?? ''}
                            placeholder={`Item ${index + 1}`}
                            onChange={(e) => handleItemDescriptionChange(index, e.target.value)}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              value={item.quantity ?? ''}
                              placeholder="1"
                              onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Price</label>
                            <input
                              type="number"
                              min="-999999"
                              step="0.01"
                              className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-right"
                              value={item.unitPrice ?? ''}
                              placeholder="0.00"
                              onChange={(e) => handleItemUnitPriceChange(index, e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Total</label>
                            <input
                              type="number"
                              min="-999999"
                              step="0.01"
                              className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-right font-medium"
                              value={item.amount ?? ''}
                              placeholder="0.00"
                              onChange={(e) => handleItemAmountChange(index, e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                          <select
                            className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={itemCategory}
                            onChange={(e) => handleItemCategoryChange(index, e.target.value)}
                          >
                            {categoryOptions.map(({ id, name }) => (
                              <option key={id} value={id}>
                                {getCategoryIcon(id)} {name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {!isIncomeUpload && (
                          <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={!!item.saveToCost}
                              onChange={(e) => handleItemSaveToggle(index, e.target.checked)}
                            />
                            Save this item to Cost Calculator
                          </label>
                        )}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleSplitLineItem(index)}
                            className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                          >
                            Split
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      );
                    })}
                    <div className="bg-gray-100 p-3 rounded-md mt-2">
                      <div className="flex justify-between items-center font-medium">
                        <span>Total Amount:</span>
                        <span>
                          {parseAmountNumber(receiptMetadata.amount) !== null
                            ? `${receiptMetadata.currency || defaultCurrencySymbol}${parseAmountNumber(receiptMetadata.amount).toFixed(2)}`
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Desktop table */}
                  <table className="min-w-full divide-y divide-gray-200 hidden md:table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        {!isIncomeUpload && (
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Save?
                          </th>
                        )}
                        <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item, index) => {
                        // Get the current category for this item and normalize it
                        const itemCategory = normalizeCategoryId(item.category || receiptMetadata.category || 'supplies');

                        return (
                          <tr 
                            key={index} 
                            className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                          >
                            <td className="px-3 py-2 text-sm text-gray-900">
                              <div>
                                <input
                                  type="text"
                                  className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                  value={item.description ?? item.name ?? item.item ?? ''}
                                  placeholder={`Item ${index + 1}`}
                                  onChange={(e) => handleItemDescriptionChange(index, e.target.value)}
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                className="w-20 py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-right"
                                value={item.quantity ?? ''}
                                placeholder="1"
                                onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                              <input
                                type="number"
                                min="-999999"
                                step="0.01"
                                className="w-24 py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-right"
                                value={item.unitPrice ?? ''}
                                placeholder="0.00"
                                onChange={(e) => handleItemUnitPriceChange(index, e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                              <input
                                type="number"
                                min="-999999"
                                step="0.01"
                                className="w-24 py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-right font-medium"
                                value={item.amount ?? ''}
                                placeholder="0.00"
                                onChange={(e) => handleItemAmountChange(index, e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">
                              <select
                                className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                value={itemCategory}
                                onChange={(e) => handleItemCategoryChange(index, e.target.value)}
                              >
                                {categoryOptions.map(({ id, name }) => (
                                  <option key={id} value={id}>
                                    {getCategoryIcon(id)} {name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            {!isIncomeUpload && (
                              <td className="px-3 py-2 text-sm text-gray-900">
                                <input
                                  type="checkbox"
                                  checked={!!item.saveToCost}
                                  onChange={(e) => handleItemSaveToggle(index, e.target.checked)}
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSplitLineItem(index)}
                                  className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100"
                                >
                                  Split
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveLineItem(index)}
                                  className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="3" className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                          Total:
                        </td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">
                          {parseAmountNumber(receiptMetadata.amount) !== null
                            ? `${receiptMetadata.currency || defaultCurrencySymbol}${parseAmountNumber(receiptMetadata.amount).toFixed(2)}`
                            : '-'}
                        </td>
                        <td colSpan="3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            

            
            {/* Action buttons */}
            <div className="mt-6 flex flex-col sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={resetForm}
                className="mb-3 sm:mb-0 w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={convertToTransaction}
                className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {bulkReceiptQueue.length > 1 && bulkReceiptIndex < bulkReceiptQueue.length - 1
                  ? 'Save and Review Next'
                  : isIncomeUpload ? 'Save Sales Invoice' : 'Save Receipt'}
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Default view: Upload a receipt or sales invoice
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {isIncomeUpload ? 'Upload a sales invoice' : 'Upload a receipt'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {isIncomeUpload
              ? 'Upload a sales invoice image or PDF to extract invoice and payment details.'
              : 'Upload a receipt image or PDF to extract transaction details.'}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <label htmlFor="file-upload" className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              isUploading || isImportingFromDrive
                ? 'bg-indigo-300 pointer-events-none'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}>
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              {isIncomeUpload ? 'Upload Sales Invoice' : 'Upload Receipt'}
            </label>
            <button
              type="button"
              onClick={handleDriveImport}
              disabled={isUploading || isImportingFromDrive}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-800 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7.71 3.5h8.58l5.71 9.9-4.29 7.1H6.29L2 13.4 7.71 3.5Zm1.15 2-4.55 7.9 3.13 5.1h9.12l3.13-5.1-4.55-7.9H8.86Z" />
                <path d="M8.75 14.2 12 8.57l3.25 5.63H8.75Z" />
              </svg>
              {isImportingFromDrive ? 'Opening Drive...' : 'Import from Drive'}
            </button>
            <input 
              id="file-upload" 
              name="file-upload" 
              type="file" 
              accept="image/jpeg,image/png,image/heic,image/heif,application/pdf"
              multiple
              className="sr-only" 
              onChange={handleFileSelect}
              ref={fileInputRef}
            />
          </div>
        </div>
      </div>
    );
  };
  
  return renderComponent();
}

export default ReceiptScannerComponent;

